import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import "./Accounting.css";

const emptyLine = () => ({
  account_id: "",
  branch_id: "",
  cost_center_id: "",
  description: "",
  debit: "",
  credit: "",
});

export default function Accounting() {
  const { hasPermission, user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const view = location.pathname.split("/")[2] || "overview";
  const subview = location.pathname.split("/")[3] || "overview";
  const [years, setYears] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [trialBalance, setTrialBalance] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankTransfers, setBankTransfers] = useState([]);
  const [bankBook, setBankBook] = useState(null);
  const [reconciliations, setReconciliations] = useState([]);
  const [bankMatchingRules, setBankMatchingRules] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [fixedAssets, setFixedAssets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberSyncing, setMemberSyncing] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [approvalWorkflows, setApprovalWorkflows] = useState([]);
  const [workflowRoles, setWorkflowRoles] = useState([]);
  const [workflowApprovers, setWorkflowApprovers] = useState([]);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [editingCostCenterId, setEditingCostCenterId] = useState(null);
  const [editingYearId, setEditingYearId] = useState(null);
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);
  const [workflowForm, setWorkflowForm] = useState({
    name: "",
    entity_type: "budget",
    minimum_amount: "",
    maximum_amount: "",
    steps: [{ name: t("Budget approval"), role_id: "", approver_user_id: "" }],
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [editingBudgetId, setEditingBudgetId] = useState(null);
  const [budgetDetails, setBudgetDetails] = useState([]);
  const [budgetImportFile, setBudgetImportFile] = useState(null);
  const [budgetImportPreview, setBudgetImportPreview] = useState(null);
  const [budgetImporting, setBudgetImporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDates, setReportDates] = useState({
    from: `${new Date().getFullYear()}-01-01`,
    to: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [journal, setJournal] = useState({
    financial_year_id: "",
    transaction_date: new Date().toISOString().slice(0, 10),
    description: "",
    reference_number: "",
    lines: [emptyLine(), emptyLine()],
  });
  const [account, setAccount] = useState({
    code: "",
    name: "",
    account_type: "asset",
    parent_id: "",
    opening_balance: 0,
  });
  const [costCenter, setCostCenter] = useState({
    code: "",
    name: "",
    branch_id: "",
  });
  const [year, setYear] = useState({ name: "", start_date: "", end_date: "" });
  const baseCashForm = {
    amount: "",
    payment_method: "bank_transfer",
    payment_reference: "",
    cash_account_id: "",
    description: "",
  };
  const [receipt, setReceipt] = useState({
    ...baseCashForm,
    payer: "",
    receipt_type: "other_income",
    receipt_date: new Date().toISOString().slice(0, 10),
    income_account_id: "",
  });
  const [payment, setPayment] = useState({
    ...baseCashForm,
    payee: "",
    payment_date: new Date().toISOString().slice(0, 10),
    expense_account_id: "",
    branch_id: "",
    cost_center_id: "",
    budget_line_id: "",
  });
  const [bankAccount, setBankAccount] = useState({
    name: "",
    account_kind: "bank",
    institution: "",
    account_number: "",
    currency: "TZS",
    ledger_account_id: "",
    opening_balance: 0,
  });
  const [reconciliation, setReconciliation] = useState({
    bank_account_id: "",
    period_start: "",
    period_end: "",
    statement_balance: "",
  });
  const [statement, setStatement] = useState({
    bank_account_id: "",
    transaction_date: "",
    reference_number: "",
    description: "",
    amount: "",
    direction: "credit",
  });
  const [transfer, setTransfer] = useState({
    transfer_date: new Date().toISOString().slice(0, 10),
    from_bank_account_id: "",
    to_bank_account_id: "",
    amount: "",
    reference_number: "",
    description: "",
  });
  const [bookFilter, setBookFilter] = useState({
    bank_account_id: "",
    from: `${new Date().getFullYear()}-01-01`,
    to: new Date().toISOString().slice(0, 10),
  });
  const [statementImportFile, setStatementImportFile] = useState(null);
  const [statementImportPreview, setStatementImportPreview] = useState(null);
  const [statementImporting, setStatementImporting] = useState(false);
  const [documentTarget, setDocumentTarget] = useState(null);
  const [financeDocuments, setFinanceDocuments] = useState([]);
  const [documentForm, setDocumentForm] = useState({ title: "", file: null });
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [matchingRule, setMatchingRule] = useState({name:"",bank_account_id:"",direction:"any",description_contains:"",date_tolerance_days:3});
  const [invoice, setInvoice] = useState({
    invoice_type: "receivable",
    customer_id: "",
    supplier_id: "",
    counterparty: "",
    invoice_date: "",
    due_date: "",
    control_account_id: "",
    description: "",
    lines: [{ description:"", quantity:1, unit_price:"", account_id:"", vat_rate:0, tax_account_id:"", withholding_rate:0, withholding_account_id:"" }],
  });
  const [settlement, setSettlement] = useState({
    settlement_date: new Date().toISOString().slice(0, 10),
    amount: "",
    cash_account_id: "",
    reference_number: "",
  });
  const [creditNote, setCreditNote] = useState({credit_date:new Date().toISOString().slice(0,10),amount:"",reason:""});
  const [purchaseOrder,setPurchaseOrder]=useState({supplier_id:"",order_date:new Date().toISOString().slice(0,10),expected_date:"",description:"",lines:[{description:"",account_id:"",quantity:1,unit_price:"",vat_rate:0}]});
  const [receivingPo,setReceivingPo]=useState(null);
  const [receiptForm,setReceiptForm]=useState({received_date:new Date().toISOString().slice(0,10),delivery_reference:"",notes:"",lines:[]});
  const [invoicingGrn,setInvoicingGrn]=useState(null);
  const [grnInvoiceForm,setGrnInvoiceForm]=useState({invoice_date:new Date().toISOString().slice(0,10),due_date:new Date(Date.now()+30*86400000).toISOString().slice(0,10),control_account_id:"",tax_account_id:""});
  const [fixedAsset,setFixedAsset]=useState({asset_code:"",name:"",description:"",purchase_date:"",in_service_date:"",cost:"",residual_value:0,useful_life_months:60,asset_account_id:"",accumulated_depreciation_account_id:"",depreciation_expense_account_id:"",branch_id:"",cost_center_id:""});
  const emptyCustomer = {
    name: "",
    customer_type: "organization",
    member_id: "",
    email: "",
    phone: "",
    address: "",
    tax_number: "",
  };
  const [customer, setCustomer] = useState(emptyCustomer);
  const [supplier, setSupplier] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    tax_number: "",
    bank_name: "",
    bank_account: "",
  });
  const [budget, setBudget] = useState({
    name: "",
    financial_year_id: "",
    branch_id: "",
    cost_center_id: "",
    lines: [
      { kind: "income", account_id: "", branch_id: "", approved_amount: "", description: "" },
      { kind: "expense", account_id: "", branch_id: "", approved_amount: "", description: "" },
    ],
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [
        y,
        a,
        j,
        t,
        r,
        p,
        b,
        br,
        inv,
        bud,
        cus,
        sup,
        bt,
        cc,
        branchesResult,
        aw,
        mem,
        per,
        matchingRulesResult,
        purchaseOrdersResult,
        fixedAssetsResult,
      ] = await Promise.all([
        api.getFinancialYears(),
        api.getAccounts(),
        api.getJournals(),
        api.getTrialBalance(),
        api.getReceipts(),
        api.getPayments(),
        api.getBankAccounts(),
        api.getReconciliations(),
        api.getInvoices(),
        api.getBudgets(),
        api.getAccountingCustomers(),
        api.getAccountingSuppliers(),
        api.getBankTransfers(),
        api.getCostCenters(),
        api.getBranches({ per_page: 100 }),
        api.getApprovalWorkflows(),
        // Members may be restricted for some finance roles; don't let that
        // fail the whole accounting page load.
        api.getMembers({ per_page: 1000, status: "active" }).catch(() => ({ data: [] })),
        api.getAccountingPeriods(),
        api.getBankMatchingRules(),
        api.getPurchaseOrders(),
        api.getFixedAssets(),
      ]);
      setYears(y.data || []);
      setAccounts(a.data || []);
      setJournals(j.data || []);
      setTrialBalance(t.data || []);
      setReceipts(r.data || []);
      setPayments(p.data || []);
      setBankAccounts(b.data || []);
      setReconciliations(br.data || []);
      setInvoices(inv.data || []);
      setBudgets(bud.data || []);
      setCustomers(cus.data || []);
      setSuppliers(sup.data || []);
      setBankTransfers(bt.data || []);
      setCostCenters(cc.data || []);
      setBranches(asArray(branchesResult.data));
      setApprovalWorkflows(aw.data || []);
      setWorkflowRoles(aw.roles || []);
      setWorkflowApprovers(aw.approvers || []);
      setMembers(mem.data || []);
      setPeriods(per.data || []);
      setBankMatchingRules(matchingRulesResult.data || []);
      setPurchaseOrders(purchaseOrdersResult.data || []);
      setFixedAssets(fixedAssetsResult.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (
      !(
        (view === "budgets" && subview === "performance") ||
        view === "payments"
      ) ||
      budgets.length === 0
    )
      return;
    Promise.all(
      budgets
        .filter((b) => view === "budgets" && subview === "performance" ? true : b.status === "approved")
        .map((b) => api.getBudget(b.id)),
    )
      .then((results) =>
        setBudgetDetails(results.map((result) => result.data).filter(Boolean)),
      )
      .catch((e) => setError(e.message));
  }, [view, subview, budgets]);
  useEffect(() => {
    if (view !== "reports" || ["overview", "trial-balance"].includes(subview))
      return;
    setReportLoading(true);
    setReportData(null);
    const params = [
      "general-ledger",
      "income-expenditure",
      "financial-position",
      "transactions",
      "audit",
      "cash-flow",
      "comparative",
    ].includes(subview)
      ? reportDates
      : subview === "contributions"
        ? { year: new Date(reportDates.to).getFullYear() }
        : {};
    const request =
      subview === "receivables-aging"
        ? api.getAccountingReport("aging", { type: "receivable" })
        : subview === "payables-aging"
          ? api.getAccountingReport("aging", { type: "payable" })
          : api.getAccountingReport(subview, params);
    request
      .then((result) => setReportData(result.data))
      .catch((e) => setError(e.message))
      .finally(() => setReportLoading(false));
  }, [view, subview, reportDates]);

  const updateLine = (index, key, value) =>
    setJournal((current) => ({
      ...current,
      lines: current.lines.map((line, i) =>
        i === index ? { ...line, [key]: value } : line,
      ),
    }));
  const saveJournal = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.createJournal(journal);
      setJournal((current) => ({
        ...current,
        description: "",
        reference_number: "",
        lines: [emptyLine(), emptyLine()],
      }));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const transition = async (id, action) => {
    let reason = null;
    if (action === "reject") {
      reason = window.prompt(t("Sababu ya kukataa journal:"));
      if (!reason) return;
    }
    try {
      await api.transitionJournal(id, action, reason);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const saveAccount = async (e) => {
    e.preventDefault();
    try {
      const data = { ...account, parent_id: account.parent_id || null };
      editingAccountId
        ? await api.updateAccount(editingAccountId, data)
        : await api.createAccount(data);
      setEditingAccountId(null);
      setAccount({
        code: "",
        name: "",
        account_type: "asset",
        parent_id: "",
        opening_balance: 0,
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const editAccount = (row) => {
    setEditingAccountId(row.id);
    setAccount({
      code: row.code,
      name: row.name,
      account_type: row.account_type,
      parent_id: row.parent_id || "",
      opening_balance: row.opening_balance || 0,
      status: row.status,
    });
  };
  const toggleAccount = async (row) => {
    try {
      await api.updateAccount(row.id, {
        status: row.status === "active" ? "inactive" : "active",
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const saveCostCenter = async (e) => {
    e.preventDefault();
    try {
      editingCostCenterId
        ? await api.updateCostCenter(editingCostCenterId, costCenter)
        : await api.createCostCenter(costCenter);
      setEditingCostCenterId(null);
      setCostCenter({ code: "", name: "", branch_id: "" });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const editCostCenter = (row) => {
    setEditingCostCenterId(row.id);
    setCostCenter({
      code: row.code,
      name: row.name,
      branch_id: row.branch_id || "",
      status: row.status,
    });
  };
  const toggleCostCenter = async (row) => {
    try {
      await api.updateCostCenter(row.id, {
        status: row.status === "active" ? "inactive" : "active",
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const saveYear = async (e) => {
    e.preventDefault();
    try {
      editingYearId
        ? await api.updateFinancialYear(editingYearId, year)
        : await api.createFinancialYear(year);
      setEditingYearId(null);
      setYear({ name: "", start_date: "", end_date: "" });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const editYear = (row) => {
    setEditingYearId(row.id);
    setYear({
      name: row.name,
      start_date: row.start_date,
      end_date: row.end_date,
    });
  };
  const yearAction = async (id, action) => {
    try {
      await api.transitionFinancialYear(id, action);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const periodAction = async (id, action) => {
    try { await api.transitionAccountingPeriod(id, action); await load(); }
    catch (err) { setError(err.message); }
  };
  const changeBudgetPeriod = (budgetId, lineId, periodId, value) => setBudgetDetails(current => current.map(b => Number(b.id) !== Number(budgetId) ? b : ({...b, lines:(b.lines||[]).map(line => Number(line.id) !== Number(lineId) ? line : ({...line, period_allocations:{...(line.period_allocations||{}), [periodId]:value}}))})));
  const saveBudgetPeriods = async (budgetRow) => {
    try { const allocations=(budgetRow.lines||[]).map(line=>({budget_line_id:line.id,periods:Object.fromEntries((budgetRow.periods||[]).map(period=>[period.id,Number(line.period_allocations?.[period.id]||0)]))}));const response=await api.saveBudgetPeriods(budgetRow.id,allocations);setBudgetDetails(current=>current.map(b=>Number(b.id)===Number(budgetRow.id)?response.data:b)); }
    catch(err){setError(err.message);}
  };
  const editWorkflow = (row) => {
    setEditingWorkflowId(row.id);
    setWorkflowForm({
      name: row.name,
      entity_type: row.entity_type,
      minimum_amount: row.minimum_amount,
      maximum_amount: row.maximum_amount ?? "",
      steps: (row.steps || []).map((s) => ({
        name: s.name,
        role_id: s.role_id || "",
        approver_user_id: s.approver_user_id || "",
      })),
    });
  };
  const saveWorkflow = async (e) => {
    e.preventDefault();
    try {
      const workflowPayload = {
        ...workflowForm,
        maximum_amount:
          Number(workflowForm.maximum_amount) > 0
            ? workflowForm.maximum_amount
            : "",
      };
      if (editingWorkflowId !== "new") await api.updateApprovalWorkflow(editingWorkflowId, workflowPayload);
      else await api.createApprovalWorkflow(workflowPayload);
      setEditingWorkflowId(null);
      setWorkflowForm({
        name: "",
        entity_type: "budget",
        minimum_amount: "",
        maximum_amount: "",
        steps: [{ name: t("Budget approval"), role_id: "", approver_user_id: "" }],
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const startWorkflow = (entityType) => {
    const labels = {
      budget: t("Budget approval"),
      staff_requisition: t("Requisition approval"),
      payment_request: t("Payment approval"),
    };
    setWorkflowForm({
      name: "",
      entity_type: entityType,
      minimum_amount: "0",
      maximum_amount: "",
      steps: [{ name: labels[entityType], role_id: "", approver_user_id: "" }],
    });
    setEditingWorkflowId("new");
  };
  const reverse = async (id) => {
    const reason = window.prompt(t("Sababu ya reversal:"));
    if (!reason) return;
    try {
      await api.reverseJournal(
        id,
        reason,
        new Date().toISOString().slice(0, 10),
      );
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const saveReceipt = async (e) => {
    e.preventDefault();
    try {
      await api.createReceipt(receipt);
      setReceipt((current) => ({
        ...current,
        payer: "",
        amount: "",
        payment_reference: "",
        description: "",
      }));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const receiptAction = async (id, action) => {
    let reason =
      action === "reject" ? window.prompt(t("Sababu ya kukataa receipt:")) : null;
    if (action === "reject" && !reason) return;
    try {
      await api.transitionReceipt(id, action, reason);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const savePayment = async (e) => {
    e.preventDefault();
    try {
      await api.createPayment(payment);
      setPayment((current) => ({
        ...current,
        payee: "",
        amount: "",
        payment_reference: "",
        description: "",
      }));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const paymentAction = async (id, action) => {
    let reason = null;
    if (action === "reject") {
      reason = window.prompt(t("Sababu ya kukataa:"));
      if (!reason) return;
    }
    try {
      await api.transitionPayment(id, action, reason);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const saveBankAccount = async (e) => {
    e.preventDefault();
    try {
      await api.createBankAccount(bankAccount);
      setBankAccount((current) => ({
        ...current,
        name: "",
        institution: "",
        account_number: "",
        ledger_account_id: "",
      }));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const saveReconciliation = async (e) => {
    e.preventDefault();
    try {
      await api.createReconciliation(reconciliation);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const saveStatement = async (e) => {
    e.preventDefault();
    try {
      const { bank_account_id, ...transaction } = statement;
      await api.addStatementTransactions(bank_account_id, [transaction]);
      setStatement((current) => ({
        ...current,
        reference_number: "",
        description: "",
        amount: "",
      }));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const reconciliationAction = async (id, action) => {
    try {
      await api.transitionReconciliation(id, action);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const openReconciliation = async (id) => {
    try {
      setSelectedReconciliation((await api.getReconciliation(id)).data);
    } catch (err) {
      setError(err.message);
    }
  };
  const saveMatchingRule = async (e) => {e.preventDefault();try{await api.createBankMatchingRule(matchingRule);setMatchingRule({name:"",bank_account_id:"",direction:"any",description_contains:"",date_tolerance_days:3});await load();}catch(err){setError(err.message);}};
  const toggleMatchingRule = async (rule) => {try{await api.updateBankMatchingRule(rule.id,{status:rule.status==="active"?"inactive":"active"});await load();}catch(err){setError(err.message);}};
  const acceptSuggestions = async () => {try{const result=await api.acceptReconciliationSuggestions(selectedReconciliation.id);setSelectedReconciliation(result.data);await load();}catch(err){setError(err.message);}};
  const runAutomaticMatching = async () => {try{const result=await api.autoMatchReconciliation(selectedReconciliation.id);setSelectedReconciliation(result.data);await load();}catch(err){setError(err.message);}};
  const savePurchaseOrder=async e=>{e.preventDefault();try{await api.createPurchaseOrder(purchaseOrder);setPurchaseOrder({supplier_id:"",order_date:new Date().toISOString().slice(0,10),expected_date:"",description:"",lines:[{description:"",account_id:"",quantity:1,unit_price:"",vat_rate:0}]});await load();}catch(err){setError(err.message);}};
  const purchaseOrderAction=async(id,action)=>{let reason=action==="reject"?window.prompt(t("Rejection reason")):null;if(action==="reject"&&!reason)return;try{await api.transitionPurchaseOrder(id,action,reason);await load();}catch(err){setError(err.message);}};
  const beginReceivePurchaseOrder=po=>{setReceivingPo(po);setReceiptForm({received_date:new Date().toISOString().slice(0,10),delivery_reference:"",notes:"",lines:po.lines.map(line=>({purchase_order_line_id:line.id,description:line.description,remaining:Number(line.quantity)-Number(line.received_quantity),quantity_received:Number(line.quantity)-Number(line.received_quantity)}))});};
  const receivePurchaseOrder=async e=>{e.preventDefault();try{await api.receivePurchaseOrder(receivingPo.id,receiptForm);setReceivingPo(null);await load();}catch(err){setError(err.message);}};
  const invoiceGrn=async e=>{e.preventDefault();try{await api.invoiceGoodsReceived(invoicingGrn.id,{...grnInvoiceForm,tax_account_id:grnInvoiceForm.tax_account_id||null});setInvoicingGrn(null);await load();}catch(err){setError(err.message);}};
  const saveFixedAsset=async e=>{e.preventDefault();try{await api.createFixedAsset(fixedAsset);setFixedAsset(current=>({...current,asset_code:"",name:"",description:"",purchase_date:"",in_service_date:"",cost:"",residual_value:0}));await load();}catch(err){setError(err.message);}};
  const depreciateAsset=async asset=>{const through=window.prompt(t("Depreciate through date"),new Date().toISOString().slice(0,10));if(!through)return;try{await api.depreciateFixedAsset(asset.id,through);await load();}catch(err){setError(err.message);}};
  const resolveReconciliationItem = async (item, mode) => {
    let data;
    if (mode === "matched") {
      const journalId = window.prompt(t("Journal entry ID to match:"), item.suggested_journal_id || "");
      if (!journalId) return;
      data = { status: "matched", journal_entry_id: journalId };
    } else {
      const notes = window.prompt(t("Resolution notes:"));
      if (!notes) return;
      data = { status: "resolved", notes };
    }
    try {
      await api.matchReconciliationItem(item.id, data);
      await openReconciliation(selectedReconciliation.id);
    } catch (err) {
      setError(err.message);
    }
  };
  const openDocuments = async (type, id, label) => {
    try {
      const result = await api.getFinanceDocuments(type, id);
      setFinanceDocuments(result.data || []);
      setDocumentTarget({ type, id, label });
      setDocumentForm({ title: "", file: null });
    } catch (err) {
      setError(err.message);
    }
  };
  const uploadDocument = async (e) => {
    e.preventDefault();
    if (!documentForm.file || !documentTarget) return;
    try {
      await api.uploadFinanceDocument(
        documentTarget.type,
        documentTarget.id,
        documentForm.file,
        documentForm.title,
      );
      await openDocuments(
        documentTarget.type,
        documentTarget.id,
        documentTarget.label,
      );
    } catch (err) {
      setError(err.message);
    }
  };
  const saveTransfer = async (e) => {
    e.preventDefault();
    try {
      await api.createBankTransfer(transfer);
      setTransfer((current) => ({
        ...current,
        from_bank_account_id: "",
        to_bank_account_id: "",
        amount: "",
        reference_number: "",
        description: "",
      }));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const transferAction = async (id, action) => {
    let reason =
      action === "reject" ? window.prompt(t("Sababu ya kukataa transfer:")) : null;
    if (action === "reject" && !reason) return;
    try {
      await api.transitionBankTransfer(id, action, reason);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const loadBankBook = async (e) => {
    if (e) e.preventDefault();
    if (!bookFilter.bank_account_id) return;
    try {
      const result = await api.getBankBook(bookFilter.bank_account_id, {
        from: bookFilter.from,
        to: bookFilter.to,
      });
      setBankBook(result.data);
    } catch (err) {
      setError(err.message);
    }
  };
  const previewStatementImport = async (e) => {
    e.preventDefault();
    if (!statementImportFile || !statement.bank_account_id) return;
    setStatementImporting(true);
    try {
      const result = await api.importBankStatement(
        statement.bank_account_id,
        statementImportFile,
        false,
      );
      setStatementImportPreview(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setStatementImporting(false);
    }
  };
  const commitStatementImport = async () => {
    if (
      !statementImportFile ||
      !statement.bank_account_id ||
      !statementImportPreview?.ready
    )
      return;
    setStatementImporting(true);
    try {
      await api.importBankStatement(
        statement.bank_account_id,
        statementImportFile,
        true,
      );
      setStatementImportFile(null);
      setStatementImportPreview(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setStatementImporting(false);
    }
  };
  const saveInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.createInvoice({
        ...invoice,
        invoice_type: view === "payables" ? "payable" : "receivable",
      });
      setInvoice((current) => ({
        ...current,
        counterparty: "",
        description: "",
        lines: [{ description:"", quantity:1, unit_price:"", account_id:"", vat_rate:0, tax_account_id:"", withholding_rate:0, withholding_account_id:"" }],
      }));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const changeInvoiceLine=(index,field,value)=>setInvoice(current=>({...current,lines:current.lines.map((line,i)=>i===index?{...line,[field]:value}:line)}));
  const invoiceTotals=(invoice.lines||[]).reduce((total,line)=>{const net=Number(line.quantity||0)*Number(line.unit_price||0),vat=net*Number(line.vat_rate||0)/100,wht=net*Number(line.withholding_rate||0)/100;return{subtotal:total.subtotal+net,vat:total.vat+vat,wht:total.wht+wht,total:total.total+net+vat-wht};},{subtotal:0,vat:0,wht:0,total:0});
  const invoiceAction = async (id, action) => {
    let reason =
      action === "reject" ? window.prompt(t("Sababu ya kukataa:")) : null;
    if (action === "reject" && !reason) return;
    try {
      await api.transitionInvoice(id, action, reason);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const openInvoice = async (id) => {
    try {
      const result = await api.getInvoice(id);
      setSelectedInvoice(result.data);
      setSettlement((current) => ({
        ...current,
        amount: (
          Number(result.data.amount) - Number(result.data.paid_amount) - Number(result.data.credited_amount || 0)
        ).toFixed(2),
      }));
    } catch (e) {
      setError(e.message);
    }
  };
  const saveCreditNote = async (e) => {e.preventDefault();try{const result=await api.createCreditNote(selectedInvoice.id,creditNote);await api.transitionCreditNote(result.data.id,"submit");setCreditNote({credit_date:new Date().toISOString().slice(0,10),amount:"",reason:""});setSelectedInvoice((await api.getInvoice(selectedInvoice.id)).data);await load();}catch(err){setError(err.message);}};
  const creditNoteAction = async (id,action) => {let reason=action==="reject"?window.prompt(t("Rejection reason")):null;if(action==="reject"&&!reason)return;try{await api.transitionCreditNote(id,action,reason);const refreshed=(await api.getInvoice(selectedInvoice.id)).data;setSelectedInvoice(refreshed);setSettlement(current=>({...current,amount:(Number(refreshed.amount)-Number(refreshed.paid_amount)-Number(refreshed.credited_amount||0)).toFixed(2)}));await load();}catch(err){setError(err.message);}};
  const saveSettlement = async (e) => {
    e.preventDefault();
    try {
      const result = await api.createInvoiceSettlement(
        selectedInvoice.id,
        settlement,
      );
      await api.transitionInvoiceSettlement(result.data.id, "submit");
      setSelectedInvoice((await api.getInvoice(selectedInvoice.id)).data);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const settlementAction = async (id, action) => {
    try {
      await api.transitionInvoiceSettlement(id, action);
      setSelectedInvoice((await api.getInvoice(selectedInvoice.id)).data);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const saveCustomer = async (e) => {
    e.preventDefault();
    try {
      await api.createAccountingCustomer(customer);
      setCustomer(emptyCustomer);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const syncMembersAsCustomers = async () => {
    setMemberSyncing(true);
    setError("");
    try {
      const res = await api.syncAccountingCustomersFromMembers();
      await load();
      if (res?.data?.created !== undefined && res.data.created === 0) {
        setError(t("Wanachama wote tayari wana rekodi ya customer (hakuna mpya)."));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setMemberSyncing(false);
    }
  };
  const linkedMemberIds = new Set(
    customers.filter((c) => c.member_id).map((c) => String(c.member_id))
  );
  const onSelectCustomerMember = (memberId) => {
    const mem = members.find((m) => String(m.id) === String(memberId));
    if (!mem) {
      setCustomer({ ...customer, member_id: "" });
      return;
    }
    const name = [mem.first_name, mem.middle_name, mem.last_name]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    setCustomer({
      ...customer,
      member_id: mem.id,
      name,
      email: mem.email || "",
      phone: mem.phone || "",
      address: mem.address || "",
    });
  };
  const saveSupplier = async (e) => {
    e.preventDefault();
    try {
      await api.createAccountingSupplier(supplier);
      setSupplier({
        name: "",
        email: "",
        phone: "",
        address: "",
        tax_number: "",
        bank_name: "",
        bank_account: "",
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };
  const saveBudget = async (e) => {
    e.preventDefault();
    try {
      if (editingBudgetId) await api.updateBudget(editingBudgetId, budget);
      else await api.createBudget(budget);
      setEditingBudgetId(null);
      setBudget({
        name: "",
        financial_year_id: "",
        branch_id: "",
        cost_center_id: "",
        lines: [
          { kind: "income", account_id: "", branch_id: "", approved_amount: "", description: "" },
          { kind: "expense", account_id: "", branch_id: "", approved_amount: "", description: "" },
        ],
      });
      await load();
      navigate("/accounting/budgets/overview");
    } catch (err) {
      setError(err.message);
    }
  };
  const editBudget = (row) => {
    setEditingBudgetId(row.id);
    setBudget({
      name: row.name,
      financial_year_id: row.financial_year_id,
      branch_id: row.branch_id || "",
      cost_center_id: row.cost_center_id || "",
      lines: (row.lines || []).map((line) => ({
        kind: line.account_type,
        account_id: line.account_id,
        branch_id: line.branch_id || row.branch_id || "",
        approved_amount: line.approved_amount,
        description: line.description || "",
      })),
    });
    navigate("/accounting/budgets/create");
  };
  const budgetAction = async (id, action) => {
    let reason =
      action === "reject" ? window.prompt(t("Sababu ya kukataa:")) : null;
    if (action === "reject" && !reason) return;
    try {
      await api.transitionBudget(id, action, reason);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const previewBudgetImport = async (e) => {
    e.preventDefault();
    if (!budgetImportFile) return;
    setBudgetImporting(true);
    setError("");
    try {
      const result = await api.importBudgets(budgetImportFile, false);
      setBudgetImportPreview(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBudgetImporting(false);
    }
  };
  const commitBudgetImport = async () => {
    if (!budgetImportFile || !budgetImportPreview?.ready) return;
    setBudgetImporting(true);
    try {
      await api.importBudgets(budgetImportFile, true);
      setBudgetImportFile(null);
      setBudgetImportPreview(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBudgetImporting(false);
    }
  };
  const money = (value) =>
    Number(value || 0).toLocaleString("en-TZ", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const budgetTotals = budgets
    .filter((item) => item.status === "approved")
    .reduce(
      (total, item) => {
        const summary = item.summary || {};
        Object.keys(total).forEach((key) => {
          total[key] += Number(summary[key] || 0);
        });
        return total;
      },
      {
        planned_income_amount: 0,
        planned_expenditure_amount: 0,
        planned_balance: 0,
        approved_amount: 0,
        actual_amount: 0,
        committed_amount: 0,
        remaining_amount: 0,
      },
    );
  // Income is recorded once per accounting scope, not once per expense line.
  // Avoid double-counting it when several budgets share the same year/branch/cost centre.
  budgetTotals.income_amount = Object.values(
    budgets
      .filter((item) => item.status === "approved")
      .reduce((scopes, item) => {
        const key = `${item.financial_year_id}|${item.branch_id || "national"}|${item.cost_center_id || "all"}`;
        scopes[key] = Number(item.summary?.income_amount || 0);
        return scopes;
      }, {}),
  ).reduce((sum, value) => sum + value, 0);
  const budgetUtilization = budgetTotals.approved_amount
    ? Math.round(
        ((budgetTotals.actual_amount + budgetTotals.committed_amount) /
          budgetTotals.approved_amount) *
          100,
      )
    : 0;

  const pageTitles = {
    overview: ["Accounting Overview", "Your finance workspace at a glance"],
    customers: ["Customers", "Debtor master records and account contacts"],
    suppliers: ["Suppliers", "Creditor master records and payment details"],
    receivables: [
      "Accounts Receivable",
      "Customer invoices, debtors and outstanding income",
    ],
    receipts: ["Receipts", "Record and manage money received"],
    payables: [
      "Accounts Payable",
      "Supplier invoices, creditors and amounts due",
    ],
    payments: ["Payments", "Payment requests and outgoing funds"],
    banking: ["Banking & Cash", "Bank accounts, statements and reconciliation"],
    "general-ledger": [
      "General Ledger",
      "Double-entry journals and posting controls",
    ],
    budgets: ["Budgets", "Plan, approve and monitor expenditure"],
    "purchase-orders": ["Purchase Orders & GRN", "Procure, receive and match supplier invoices"],
    "fixed-assets": ["Fixed Assets Register", "Track assets, book values and depreciation"],
    reports: ["Financial Reports", "Trial balance and management reporting"],
    settings: ["Accounting Settings", "Chart of accounts and financial years"],
  };
  const title = pageTitles[view] || pageTitles.overview;
  const outstandingReceivable = invoices
    .filter((i) => i.invoice_type === "receivable")
    .reduce((sum, i) => sum + Number(i.amount) - Number(i.paid_amount) - Number(i.credited_amount || 0), 0);
  const outstandingPayable = invoices
    .filter((i) => i.invoice_type === "payable")
    .reduce((sum, i) => sum + Number(i.amount) - Number(i.paid_amount) - Number(i.credited_amount || 0), 0);
  return (
    <div className="accounting-page sage-accounting">
      <div className="accounting-header">
        <button
          type="button"
          className="accounting-back"
          aria-label={t("Back")}
          onClick={() =>
            navigate(
              subview !== "overview"
                ? `/accounting/${view}`
                : view !== "overview"
                  ? "/accounting/overview"
                  : "/",
            )
          }
        >
          ←
        </button>
        <div>
          <span className="accounting-eyebrow">TAREWU FINANCE</span>
          <h1>{t(title[0])}</h1>
          <p>{t(title[1])}</p>
        </div>
        <div className="accounting-period">{t("Financial workspace")}</div>
      </div>
      <nav className="accounting-module-nav" aria-label={t("Accounting modules")}>
        {accountingModules.map((m) => (
          <Link
            key={m.to}
            to={m.to}
            className={m.views.includes(view) ? "active" : ""}
          >
            <i aria-hidden="true">{m.icon}</i>
            {t(m.label)}
          </Link>
        ))}
      </nav>
      {(receivableViews.includes(view) || payableViews.includes(view)) && (
        <nav className="accounting-section-nav budget-nav">
          {(receivableViews.includes(view)
            ? [["customers", "Customers"], ["receivables", "Customer Invoices"], ["receipts", "Receipts"]]
            : [["suppliers", "Suppliers"], ["payables", "Supplier Invoices"], ["payments", "Payments"]]
          ).map(([key, label]) => (
            <Link key={key} className={view === key ? "active" : ""} to={`/accounting/${key}`}>
              {t(label)}
            </Link>
          ))}
        </nav>
      )}
      {view === "budgets" && (
        <nav className="accounting-section-nav budget-nav">
          <Link
            className={subview === "overview" ? "active" : ""}
            to="/accounting/budgets"
          >
            {t("Budget Overview")}
          </Link>
          <Link
            className={subview === "create" ? "active" : ""}
            to="/accounting/budgets/create"
          >
            {t("Create Budget")}
          </Link>
          <Link
            className={subview === "import" ? "active" : ""}
            to="/accounting/budgets/import"
          >
            {t("Import Budget")}
          </Link>
          <Link
            className={subview === "approvals" ? "active" : ""}
            to="/accounting/budgets/approvals"
          >
            {t("Approvals")}
          </Link>
          <Link
            className={subview === "performance" ? "active" : ""}
            to="/accounting/budgets/performance"
          >
            {t("Budget vs Actual")}
          </Link>
        </nav>
      )}
      {view === "reports" && (
        <nav className="accounting-section-nav budget-nav">
          <Link
            className={
              ["overview", "trial-balance"].includes(subview) ? "active" : ""
            }
            to="/accounting/reports/trial-balance"
          >
            {t("Trial Balance")}
          </Link>
          <Link
            className={subview === "general-ledger" ? "active" : ""}
            to="/accounting/reports/general-ledger"
          >
            {t("General Ledger")}
          </Link>
          <Link
            className={subview === "income-expenditure" ? "active" : ""}
            to="/accounting/reports/income-expenditure"
          >
            {t("Income & Expenditure")}
          </Link>
          <Link
            className={subview === "financial-position" ? "active" : ""}
            to="/accounting/reports/financial-position"
          >
            {t("Financial Position")}
          </Link>
          <Link
            className={subview === "cash-bank" ? "active" : ""}
            to="/accounting/reports/cash-bank"
          >
            {t("Cash & Bank")}
          </Link>
          <Link className={subview === "cash-flow" ? "active" : ""} to="/accounting/reports/cash-flow">{t("Cash Flow")}</Link>
          <Link className={subview === "comparative" ? "active" : ""} to="/accounting/reports/comparative">{t("Comparative")}</Link>
          <Link
            className={subview === "receivables-aging" ? "active" : ""}
            to="/accounting/reports/receivables-aging"
          >
            {t("AR Aging")}
          </Link>
          <Link
            className={subview === "payables-aging" ? "active" : ""}
            to="/accounting/reports/payables-aging"
          >
            {t("AP Aging")}
          </Link>
          <Link
            className={subview === "transactions" ? "active" : ""}
            to="/accounting/reports/transactions"
          >
            {t("Transactions")}
          </Link>
          <Link
            className={subview === "contributions" ? "active" : ""}
            to="/accounting/reports/contributions"
          >
            {t("Contributions")}
          </Link>
          <Link
            className={subview === "audit" ? "active" : ""}
            to="/accounting/reports/audit"
          >
            {t("Audit")}
          </Link>
        </nav>
      )}
      {view === "banking" && (
        <nav className="accounting-section-nav budget-nav">
          <Link
            className={subview === "overview" ? "active" : ""}
            to="/accounting/banking"
          >
            {t("Accounts")}
          </Link>
          <Link
            className={subview === "transfers" ? "active" : ""}
            to="/accounting/banking/transfers"
          >
            {t("Transfers")}
          </Link>
          <Link
            className={subview === "book" ? "active" : ""}
            to="/accounting/banking/book"
          >
            {t("Cash / Bank Book")}
          </Link>
          <Link
            className={subview === "statement-import" ? "active" : ""}
            to="/accounting/banking/statement-import"
          >
            {t("Statement Import")}
          </Link>
          <Link
            className={subview === "reconciliation" ? "active" : ""}
            to="/accounting/banking/reconciliation"
          >
            {t("Reconciliation")}
          </Link>
        </nav>
      )}
      {view === "settings" && (
        <nav className="accounting-section-nav budget-nav">
          <Link
            className={
              ["overview", "chart-of-accounts"].includes(subview)
                ? "active"
                : ""
            }
            to="/accounting/settings/chart-of-accounts"
          >
            {t("Chart of Accounts")}
          </Link>
          <Link
            className={subview === "cost-centres" ? "active" : ""}
            to="/accounting/settings/cost-centres"
          >
            {t("Cost Centres")}
          </Link>
          <Link
            className={subview === "financial-years" ? "active" : ""}
            to="/accounting/settings/financial-years"
          >
            {t("Financial Years")}
          </Link>
          <Link
            className={subview === "approval-hierarchy" ? "active" : ""}
            to="/accounting/settings/approval-hierarchy"
          >
            {t("Approval Hierarchy")}
          </Link>
        </nav>
      )}
      {error && <div className="accounting-error">{error}</div>}
      {loading ? (
        <p>{t("Loading accounting data…")}</p>
      ) : (
        <>
          {view === "overview" && (
            <section>
              <div className="finance-kpis">
                <Kpi
                  label={t("Money owed to you")}
                  value={`TZS ${money(outstandingReceivable)}`}
                  tone="green"
                />
                <Kpi
                  label={t("Money you owe")}
                  value={`TZS ${money(outstandingPayable)}`}
                  tone="orange"
                />
                <Kpi
                  label={t("Bank & cash accounts")}
                  value={bankAccounts.length}
                  tone="blue"
                />
                <Kpi
                  label={t("Journals awaiting action")}
                  value={
                    journals.filter((j) =>
                      ["draft", "submitted", "approved"].includes(j.status),
                    ).length
                  }
                  tone="purple"
                />
              </div>
              <div className="finance-workspaces">
                <Workspace
                  to="/accounting/receivables"
                  title={t("Accounts Receivable")}
                  text={t("Sales invoices, receipts and debtor balances")}
                  icon="↓"
                />
                <Workspace
                  to="/accounting/payables"
                  title={t("Accounts Payable")}
                  text={t("Supplier invoices, payments and creditor balances")}
                  icon="↑"
                />
                <Workspace
                  to="/accounting/banking"
                  title={t("Banking")}
                  text={t("Statements, cash accounts and reconciliation")}
                  icon="▤"
                />
                <Workspace
                  to="/accounting/general-ledger"
                  title={t("General Ledger")}
                  text={t("Journals, chart of accounts and posting")}
                  icon="≡"
                />
                <Workspace
                  to="/accounting/budgets"
                  title={t("Budgets")}
                  text={t("Approved limits and actual expenditure")}
                  icon="◎"
                />
                <Workspace
                  to="/accounting/reports"
                  title={t("Financial Reports")}
                  text={t("Trial balance and finance reporting")}
                  icon="▥"
                />
              </div>
            </section>
          )}
          {view === "general-ledger" && (
            <section>
              {hasPermission("finance.create") && (
                <form className="accounting-card" onSubmit={saveJournal}>
                  <h2>{t("New journal")}</h2>
                  <div className="accounting-grid">
                    <select
                      required
                      value={journal.financial_year_id}
                      onChange={(e) =>
                        setJournal({
                          ...journal,
                          financial_year_id: e.target.value,
                        })
                      }
                    >
                      <option value="">{t("Financial year")}</option>
                      {years
                        .filter((y) => y.status === "open")
                        .map((y) => (
                          <option key={y.id} value={y.id}>
                            {y.name}
                          </option>
                        ))}
                    </select>
                    <input
                      type="date"
                      required
                      value={journal.transaction_date}
                      onChange={(e) =>
                        setJournal({
                          ...journal,
                          transaction_date: e.target.value,
                        })
                      }
                    />
                    <input
                      placeholder={t("Reference")}
                      value={journal.reference_number}
                      onChange={(e) =>
                        setJournal({
                          ...journal,
                          reference_number: e.target.value,
                        })
                      }
                    />
                    <input
                      required
                      placeholder={t("Description")}
                      value={journal.description}
                      onChange={(e) =>
                        setJournal({ ...journal, description: e.target.value })
                      }
                    />
                  </div>
                  {journal.lines.map((line, i) => (
                    <div className="journal-line" key={i}>
                      <select
                        required
                        value={line.account_id}
                        onChange={(e) =>
                          updateLine(i, "account_id", e.target.value)
                        }
                      >
                        <option value="">{t("Account")}</option>
                        {accounts
                          .filter((a) => a.status === "active")
                          .map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.code} — {a.name}
                            </option>
                          ))}
                      </select>
                      <select
                        value={line.branch_id}
                        onChange={(e) =>
                          updateLine(i, "branch_id", e.target.value)
                        }
                      >
                        <option value="">{t("National / no branch")}</option>
                        {branches
                          .filter((b) => b.status === "active")
                          .map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.code} — {b.name}
                            </option>
                          ))}
                      </select>
                      <select
                        value={line.cost_center_id}
                        onChange={(e) =>
                          updateLine(i, "cost_center_id", e.target.value)
                        }
                      >
                        <option value="">{t("No cost centre")}</option>
                        {costCenters
                          .filter(
                            (c) =>
                              c.status === "active" &&
                              (!line.branch_id ||
                                String(c.branch_id) === String(line.branch_id)),
                          )
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.code} — {c.name}
                            </option>
                          ))}
                      </select>
                      <input
                        placeholder={t("Line description")}
                        value={line.description}
                        onChange={(e) =>
                          updateLine(i, "description", e.target.value)
                        }
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={t("Debit")}
                        value={line.debit}
                        onChange={(e) => updateLine(i, "debit", e.target.value)}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder={t("Credit")}
                        value={line.credit}
                        onChange={(e) =>
                          updateLine(i, "credit", e.target.value)
                        }
                      />
                      {journal.lines.length > 2 && (
                        <button
                          type="button"
                          onClick={() =>
                            setJournal({
                              ...journal,
                              lines: journal.lines.filter((_, x) => x !== i),
                            })
                          }
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={() =>
                        setJournal({
                          ...journal,
                          lines: [...journal.lines, emptyLine()],
                        })
                      }
                    >
                      {t("Add line")}
                    </button>
                    <button className="primary" type="submit">
                      {t("Save draft")}
                    </button>
                  </div>
                </form>
              )}
              <Table
                headers={[t("Number"), t("Date"), t("Description"), t("Status"), t("Actions")]}
                rows={journals.map((j) => [
                  j.journal_number,
                  j.transaction_date,
                  j.description,
                  <span className={`status ${j.status}`}>{j.status}</span>,
                  <div className="row-actions">
                    <button
                      onClick={() =>
                        openDocuments("journal", j.id, j.journal_number)
                      }
                    >
                      {t("Documents")}
                    </button>
                    {j.status === "draft" &&
                      hasPermission("finance.create") && (
                        <button onClick={() => transition(j.id, "submit")}>
                          {t("Submit")}
                        </button>
                      )}
                    {j.status === "submitted" &&
                      hasPermission("finance.approve") && (
                        <>
                          <button onClick={() => transition(j.id, "approve")}>
                            {t("Approve")}
                          </button>
                          <button onClick={() => transition(j.id, "reject")}>
                            {t("Reject")}
                          </button>
                        </>
                      )}
                    {j.status === "approved" &&
                      hasPermission("finance.post") && (
                        <button onClick={() => transition(j.id, "post")}>
                          {t("Post")}
                        </button>
                      )}
                    {j.status === "posted" &&
                      hasPermission("finance.reverse") && (
                        <button onClick={() => reverse(j.id)}>{t("Reverse")}</button>
                      )}
                  </div>,
                ])}
              />
            </section>
          )}
          {view === "receipts" && (
            <section>
              {hasPermission("finance.create") && (
                <CashForm
                  kind="receipt"
                  value={receipt}
                  setValue={setReceipt}
                  accounts={accounts}
                  onSubmit={saveReceipt}
                />
              )}
              <Table
                headers={[
                  t("Receipt"),
                  t("Date"),
                  t("Payer"),
                  t("Amount"),
                  t("Method"),
                  t("Status"),
                  t("Actions"),
                ]}
                rows={receipts.map((r) => [
                  r.receipt_number,
                  r.receipt_date,
                  r.payer,
                  money(r.amount),
                  r.payment_method,
                  r.status,
                  <div className="row-actions">
                    {r.status === "draft" &&
                      hasPermission("finance.create") && (
                        <button onClick={() => receiptAction(r.id, "submit")}>
                          {t("Submit")}
                        </button>
                      )}
                    {r.status === "submitted" &&
                      hasPermission("finance.approve") && (
                        <>
                          <button
                            onClick={() => receiptAction(r.id, "approve")}
                          >
                            {t("Approve")}
                          </button>
                          <button onClick={() => receiptAction(r.id, "reject")}>
                            {t("Reject")}
                          </button>
                        </>
                      )}
                    {r.status === "approved" &&
                      hasPermission("finance.post") && (
                        <button onClick={() => receiptAction(r.id, "post")}>
                          {t("Post")}
                        </button>
                      )}
                    {r.status === "rejected" &&
                      hasPermission("finance.create") && (
                        <button onClick={() => receiptAction(r.id, "return")}>
                          {t("Return to draft")}
                        </button>
                      )}
                    <button
                      onClick={() =>
                        openDocuments("receipt", r.id, r.receipt_number)
                      }
                    >
                      {t("Documents")}
                    </button>
                    <button
                      onClick={() =>
                        api.printReceipt(r.id).catch((e) => setError(e.message))
                      }
                    >
                      {t("Print")}
                    </button>
                  </div>,
                ])}
              />
            </section>
          )}
          {view === "payments" && (
            <section>
              {hasPermission("finance.create") && (
                <CashForm
                  kind="payment"
                  value={payment}
                  setValue={setPayment}
                  accounts={accounts}
                  branches={branches}
                  costCenters={costCenters}
                  budgetDetails={budgetDetails}
                  onSubmit={savePayment}
                />
              )}
              <Table
                headers={[
                  t("Payment"),
                  t("Date"),
                  t("Payee"),
                  t("Amount"),
                  t("Approval step"),
                  t("Status"),
                  t("Actions"),
                ]}
                rows={payments.map((p) => [
                  p.payment_number,
                  p.payment_date,
                  p.payee,
                  money(p.amount),
                  p.approval?.status === "pending"
                    ? p.approval.current_step?.role_name ||
                      p.approval.current_step?.name
                    : p.approval?.status || "-",
                  p.status,
                  <div className="row-actions">
                    {p.status === "draft" &&
                      hasPermission("finance.create") && (
                        <button onClick={() => paymentAction(p.id, "submit")}>
                          {t("Submit")}
                        </button>
                      )}
                    {p.status === "submitted" &&
                      hasPermission("finance.approve") && (
                        <>
                          <button
                            onClick={() => paymentAction(p.id, "approve")}
                          >
                            {t("Approve current step")}
                          </button>
                          <button onClick={() => paymentAction(p.id, "reject")}>
                            {t("Reject")}
                          </button>
                        </>
                      )}
                    {p.status === "rejected" &&
                      hasPermission("finance.create") && (
                        <button onClick={() => paymentAction(p.id, "return")}>
                          {t("Return to draft")}
                        </button>
                      )}
                    {p.status === "approved" &&
                      hasPermission("finance.post") && (
                        <button onClick={() => paymentAction(p.id, "pay")}>
                          {t("Confirm paid")}
                        </button>
                      )}
                    <button
                      onClick={() =>
                        openDocuments("payment", p.id, p.payment_number)
                      }
                    >
                      {t("Documents")}
                    </button>
                    <button
                      onClick={() =>
                        api.printPayment(p.id).catch((e) => setError(e.message))
                      }
                    >
                      {t("Voucher")}
                    </button>
                  </div>,
                ])}
              />
            </section>
          )}
          {view === "customers" && (
            <section>
              {hasPermission("finance.create") && (
                <>
                  <div
                    className="accounting-card"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <strong>{t("Wanachama ndio wateja.")}</strong>{" "}
                      {t(
                        "Wanachama ambao bado hawana rekodi ya mteja wanaweza kuongezwa kwa kubofya kitufe kimoja badala ya kuandikwa upya mmoja mmoja."
                      )}
                    </div>
                    <button
                      type="button"
                      className="primary"
                      disabled={memberSyncing}
                      onClick={syncMembersAsCustomers}
                    >
                      {memberSyncing
                        ? t("Inasawazisha...")
                        : t("Sync members → customers")}
                    </button>
                  </div>
                  <form
                    className="accounting-card accounting-grid"
                    onSubmit={saveCustomer}
                  >
                    <select
                      value={customer.customer_type}
                      onChange={(e) => {
                        const customer_type = e.target.value;
                        setCustomer(
                          customer_type === "member"
                            ? { ...emptyCustomer, customer_type }
                            : { ...customer, customer_type, member_id: "" }
                        );
                      }}
                    >
                      <option value="organization">{t("Organization")}</option>
                      <option value="individual">{t("Individual")}</option>
                      <option value="member">{t("Member")}</option>
                    </select>
                    {customer.customer_type === "member" ? (
                      <select
                        required
                        value={customer.member_id}
                        onChange={(e) => onSelectCustomerMember(e.target.value)}
                      >
                        <option value="">{t("Select member…")}</option>
                        {members.map((m) => {
                          const isLinked = linkedMemberIds.has(String(m.id));
                          const label = [m.first_name, m.last_name]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <option
                              key={m.id}
                              value={m.id}
                              disabled={isLinked}
                            >
                              {m.member_number} — {label}
                              {isLinked ? ` (${t("already linked")})` : ""}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <input
                        required
                        placeholder={t("Customer name")}
                        value={customer.name}
                        onChange={(e) =>
                          setCustomer({ ...customer, name: e.target.value })
                        }
                      />
                    )}
                    <input
                      type="email"
                      placeholder={t("Email")}
                      value={customer.email}
                      onChange={(e) =>
                        setCustomer({ ...customer, email: e.target.value })
                      }
                    />
                    <input
                      placeholder={t("Phone")}
                      value={customer.phone}
                      onChange={(e) =>
                        setCustomer({ ...customer, phone: e.target.value })
                      }
                    />
                    <input
                      placeholder={t("Address")}
                      value={customer.address}
                      onChange={(e) =>
                        setCustomer({ ...customer, address: e.target.value })
                      }
                    />
                    <input
                      placeholder={t("TIN / Registration number")}
                      value={customer.tax_number}
                      onChange={(e) =>
                        setCustomer({
                          ...customer,
                          tax_number: e.target.value,
                        })
                      }
                    />
                    <button
                      className="primary"
                      disabled={
                        customer.customer_type === "member" &&
                        !customer.member_id
                      }
                    >
                      {t("Add customer")}
                    </button>
                  </form>
                </>
              )}
              <Table
                pageSize={10}
                headers={[
                  t("Number"),
                  t("Customer"),
                  t("Type"),
                  t("Phone"),
                  t("Email"),
                  t("Status"),
                ]}
                rows={customers.map((c) => [
                  c.customer_number,
                  c.member_id ? `${c.name} 🔗` : c.name,
                  c.customer_type,
                  c.phone || "-",
                  c.email || "-",
                  c.status,
                ])}
              />
            </section>
          )}
          {view === "suppliers" && (
            <section>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card accounting-grid"
                  onSubmit={saveSupplier}
                >
                  <input
                    required
                    placeholder={t("Supplier name")}
                    value={supplier.name}
                    onChange={(e) =>
                      setSupplier({ ...supplier, name: e.target.value })
                    }
                  />
                  <input
                    type="email"
                    placeholder={t("Email")}
                    value={supplier.email}
                    onChange={(e) =>
                      setSupplier({ ...supplier, email: e.target.value })
                    }
                  />
                  <input
                    placeholder={t("Phone")}
                    value={supplier.phone}
                    onChange={(e) =>
                      setSupplier({ ...supplier, phone: e.target.value })
                    }
                  />
                  <input
                    placeholder={t("Address")}
                    value={supplier.address}
                    onChange={(e) =>
                      setSupplier({ ...supplier, address: e.target.value })
                    }
                  />
                  <input
                    placeholder={t("TIN / Registration number")}
                    value={supplier.tax_number}
                    onChange={(e) =>
                      setSupplier({ ...supplier, tax_number: e.target.value })
                    }
                  />
                  <input
                    placeholder={t("Bank name")}
                    value={supplier.bank_name}
                    onChange={(e) =>
                      setSupplier({ ...supplier, bank_name: e.target.value })
                    }
                  />
                  <input
                    placeholder={t("Bank account")}
                    value={supplier.bank_account}
                    onChange={(e) =>
                      setSupplier({ ...supplier, bank_account: e.target.value })
                    }
                  />
                  <button className="primary">{t("Add supplier")}</button>
                </form>
              )}
              <Table
                headers={[
                  t("Number"),
                  t("Supplier"),
                  t("Phone"),
                  t("Email"),
                  t("Bank"),
                  t("Status"),
                ]}
                rows={suppliers.map((s) => [
                  s.supplier_number,
                  s.name,
                  s.phone || "-",
                  s.email || "-",
                  s.bank_name || "-",
                  s.status,
                ])}
              />
            </section>
          )}
          {["receivables", "payables"].includes(view) && (
            <section>
              <div className="section-action-bar">
                <div>
                  <h2>
                    {view === "receivables"
                      ? t("Customer invoices")
                      : t("Supplier invoices")}
                  </h2>
                  <p>
                    {view === "receivables"
                      ? t("Track amounts owed to TAREWU.")
                      : t("Track amounts TAREWU owes suppliers.")}
                  </p>
                </div>
                <Link
                  className="secondary-link"
                  to={
                    view === "receivables"
                      ? "/accounting/receipts"
                      : "/accounting/payments"
                  }
                >
                  {view === "receivables" ? t("View receipts") : t("View payments")} →
                </Link>
              </div>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card accounting-grid"
                  onSubmit={saveInvoice}
                >
                  {view === "receivables" ? (
                    <select
                      required
                      value={invoice.customer_id}
                      onChange={(e) =>
                        setInvoice({ ...invoice, customer_id: e.target.value })
                      }
                    >
                      <option value="">{t("Select customer")}</option>
                      {customers
                        .filter((c) => c.status === "active")
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.customer_number} — {c.name}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <select
                      required
                      value={invoice.supplier_id}
                      onChange={(e) =>
                        setInvoice({ ...invoice, supplier_id: e.target.value })
                      }
                    >
                      <option value="">{t("Select supplier")}</option>
                      {suppliers
                        .filter((s) => s.status === "active")
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.supplier_number} — {s.name}
                          </option>
                        ))}
                    </select>
                  )}
                  <input
                    required
                    type="date"
                    value={invoice.invoice_date}
                    onChange={(e) =>
                      setInvoice({ ...invoice, invoice_date: e.target.value })
                    }
                  />
                  <input
                    required
                    type="date"
                    value={invoice.due_date}
                    onChange={(e) =>
                      setInvoice({ ...invoice, due_date: e.target.value })
                    }
                  />
                  <AccountSelect
                    label={t("Control account")}
                    value={invoice.control_account_id}
                    accounts={accounts.filter(
                      (a) =>
                        a.account_type ===
                        (view === "receivables" ? "asset" : "liability"),
                    )}
                    onChange={(v) =>
                      setInvoice({ ...invoice, control_account_id: v })
                    }
                  />
                  <input
                    required
                    placeholder={t("Description")}
                    value={invoice.description}
                    onChange={(e) =>
                      setInvoice({ ...invoice, description: e.target.value })
                    }
                  />
                  <div className="budget-plan-editor">
                    <div className="budget-plan-heading"><div><h3>{t("Invoice lines")}</h3><p>{t("Quantity, price, nominal account, VAT and withholding tax are calculated automatically.")}</p></div><button type="button" onClick={()=>setInvoice({...invoice,lines:[...invoice.lines,{description:"",quantity:1,unit_price:"",account_id:"",vat_rate:0,tax_account_id:"",withholding_rate:0,withholding_account_id:""}]})}>+ {t("Add line")}</button></div>
                    {invoice.lines.map((line,index)=><div className="budget-plan-line" key={index}>
                      <input required placeholder={t("Line description")} value={line.description} onChange={e=>changeInvoiceLine(index,"description",e.target.value)}/>
                      <input required type="number" min="0.001" step="0.001" placeholder={t("Quantity")} value={line.quantity} onChange={e=>changeInvoiceLine(index,"quantity",e.target.value)}/>
                      <input required type="number" min="0" step="0.01" placeholder={t("Unit price")} value={line.unit_price} onChange={e=>changeInvoiceLine(index,"unit_price",e.target.value)}/>
                      <AccountSelect label={view==="receivables"?t("Income account"):t("Expense / asset account")} value={line.account_id} accounts={accounts.filter(a=>(view==="receivables"?a.account_type==="income":["expense","asset"].includes(a.account_type))&&a.status==="active")} onChange={v=>changeInvoiceLine(index,"account_id",v)}/>
                      <label>{t("VAT %")}<input type="number" min="0" max="100" step="0.001" value={line.vat_rate} onChange={e=>changeInvoiceLine(index,"vat_rate",e.target.value)}/></label>
                      {Number(line.vat_rate)>0&&<AccountSelect label={t("VAT account")} value={line.tax_account_id} accounts={accounts.filter(a=>a.account_type===(view==="receivables"?"liability":"asset")&&a.status==="active")} onChange={v=>changeInvoiceLine(index,"tax_account_id",v)}/>} 
                      <label>{t("Withholding %")}<input type="number" min="0" max="100" step="0.001" value={line.withholding_rate} onChange={e=>changeInvoiceLine(index,"withholding_rate",e.target.value)}/></label>
                      {Number(line.withholding_rate)>0&&<AccountSelect label={t("Withholding account")} value={line.withholding_account_id} accounts={accounts.filter(a=>a.account_type===(view==="receivables"?"asset":"liability")&&a.status==="active")} onChange={v=>changeInvoiceLine(index,"withholding_account_id",v)}/>} 
                      <strong>TZS {money(Number(line.quantity||0)*Number(line.unit_price||0))}</strong>{invoice.lines.length>1&&<button type="button" onClick={()=>setInvoice({...invoice,lines:invoice.lines.filter((_,i)=>i!==index)})}>×</button>}
                    </div>)}
                    <div className="budget-plan-totals"><span>{t("Subtotal")}: <b>TZS {money(invoiceTotals.subtotal)}</b></span><span>{t("VAT")}: <b>TZS {money(invoiceTotals.vat)}</b></span><span>{t("Withholding")}: <b>TZS {money(invoiceTotals.wht)}</b></span><span>{t("Amount payable")}: <b>TZS {money(invoiceTotals.total)}</b></span></div>
                  </div>
                  <button className="primary">{t("Create invoice")}</button>
                </form>
              )}
              <Table
                headers={[
                  t("Invoice"),
                  view === "receivables" ? t("Customer") : t("Supplier"),
                  t("Due"),
                  t("Amount"),
                  t("Outstanding"),
                  t("Status"),
                  t("Actions"),
                ]}
                rows={invoices
                  .filter(
                    (i) =>
                      i.invoice_type ===
                      (view === "receivables" ? "receivable" : "payable"),
                  )
                  .map((i) => [
                    i.invoice_number,
                    i.counterparty,
                    i.due_date,
                    money(i.amount),
                    money(Number(i.amount) - Number(i.paid_amount) - Number(i.credited_amount || 0)),
                    i.status,
                    <div className="row-actions">
                      <button onClick={() => openInvoice(i.id)}>{t("View")}</button>
                      <button
                        onClick={() =>
                          openDocuments("invoice", i.id, i.invoice_number)
                        }
                      >
                        {t("Documents")}
                      </button>
                      {i.status === "draft" &&
                        hasPermission("finance.create") && (
                          <button onClick={() => invoiceAction(i.id, "submit")}>
                            {t("Submit")}
                          </button>
                        )}
                      {i.status === "submitted" &&
                        hasPermission("finance.approve") && (
                          <>
                            <button
                              onClick={() => invoiceAction(i.id, "approve")}
                            >
                              {t("Approve")}
                            </button>
                            <button
                              onClick={() => invoiceAction(i.id, "reject")}
                            >
                              {t("Reject")}
                            </button>
                          </>
                        )}
                      {i.status === "rejected" &&
                        hasPermission("finance.create") && (
                          <button onClick={() => invoiceAction(i.id, "return")}>
                            {t("Return to draft")}
                          </button>
                        )}
                    </div>,
                  ])}
              />
              {selectedInvoice &&
                selectedInvoice.invoice_type ===
                  (view === "receivables" ? "receivable" : "payable") && (
                  <InvoiceDetail
                    invoice={selectedInvoice}
                    accounts={accounts}
                    settlement={settlement}
                    setSettlement={setSettlement}
                    creditNote={creditNote}
                    setCreditNote={setCreditNote}
                    onClose={() => setSelectedInvoice(null)}
                    onSaveSettlement={saveSettlement}
                    onSettlementAction={settlementAction}
                    onSaveCreditNote={saveCreditNote}
                    onCreditNoteAction={creditNoteAction}
                    hasPermission={hasPermission}
                    money={money}
                  />
                )}
            </section>
          )}
          {view === "budgets" && subview === "overview" && (
            <section>
              <div className="budget-financial-summary">
                <Kpi
                  label={t("Planned income")}
                  value={`TZS ${money(budgetTotals.planned_income_amount)}`}
                  tone="green"
                />
                <Kpi
                  label={t("Income collected")}
                  value={`TZS ${money(budgetTotals.income_amount)}`}
                  tone="blue"
                />
                <Kpi
                  label={t("Planned expenditure")}
                  value={`TZS ${money(budgetTotals.planned_expenditure_amount)}`}
                  tone="purple"
                />
                <Kpi
                  label={t("Actual expenditure")}
                  value={`TZS ${money(budgetTotals.actual_amount)}`}
                  tone="orange"
                />
                <Kpi
                  label={t("Committed")}
                  value={`TZS ${money(budgetTotals.committed_amount)}`}
                  tone="purple"
                />
                <Kpi
                  label={t("Expenditure balance")}
                  value={`TZS ${money(budgetTotals.remaining_amount)}`}
                  tone={budgetTotals.remaining_amount < 0 ? "red" : "green"}
                />
                <Kpi
                  label={t("Planned surplus / deficit")}
                  value={`TZS ${money(budgetTotals.planned_balance)}`}
                  tone={budgetTotals.planned_balance < 0 ? "red" : "blue"}
                />
              </div>
              <div className="budget-utilization-card">
                <div>
                  <strong>{t("Budget utilization")}</strong>
                  <span>{budgetUtilization}% {t("used and committed")}</span>
                </div>
                <div className="budget-progress" aria-label={`${budgetUtilization}%`}>
                  <span style={{ width: `${Math.min(Math.max(budgetUtilization, 0), 100)}%` }} />
                </div>
                <small>
                  {t("Available after expenditure and commitments")}: TZS {money(budgetTotals.remaining_amount)}
                </small>
              </div>
              <div className="section-action-bar">
                <div>
                  <h2>{t("Budget register")}</h2>
                  <p>{t("Budgets grouped by financial year and approval status.")}</p>
                </div>
                {hasPermission("finance.create") && (
                  <Link
                    className="secondary-link"
                    to="/accounting/budgets/create"
                  >
                    {t("Create budget")} →
                  </Link>
                )}
              </div>
              <Table
                headers={[t("Budget"), t("Financial Year"), t("Branch"), t("Planned income"), t("Planned expense"), t("Planned balance"), t("Collected"), t("Spent"), t("Expense balance"), t("Status"), t("Actions")]}
                rows={budgets.map((b) => [
                  <span><b>{b.budget_code || "—"}</b><br/><small>{b.name}</small></span>,
                  years.find((year) => String(year.id) === String(b.financial_year_id))?.name || b.financial_year_id,
                  branches.find((branch) => String(branch.id) === String(b.branch_id))?.name || t("National"),
                  money(b.summary?.planned_income_amount),
                  money(b.summary?.planned_expenditure_amount),
                  <span className={Number(b.summary?.planned_balance) < 0 ? "budget-negative" : "budget-positive"}>{money(b.summary?.planned_balance)}</span>,
                  money(b.summary?.income_amount),
                  money(b.summary?.actual_amount),
                  <span className={Number(b.summary?.remaining_amount) < 0 ? "budget-negative" : "budget-positive"}>{money(b.summary?.remaining_amount)}</span>,
                  b.status,
                  <div className="row-actions">
                    {["draft", "rejected"].includes(b.status) &&
                      (hasPermission("finance.create") || hasPermission("finance.approve")) && (
                        <button onClick={() => editBudget(b)}>{t("Edit")}</button>
                      )}
                    {b.status === "draft" &&
                      hasPermission("finance.create") && (
                        <button onClick={() => budgetAction(b.id, "submit")}>
                          {t("Submit")}
                        </button>
                      )}
                    {b.status === "submitted" && b.approval?.current_step?.approver_user_id && <small>{t("Awaiting")}: {workflowApprovers.find(item=>String(item.id)===String(b.approval.current_step.approver_user_id))?.email || `User #${b.approval.current_step.approver_user_id}`}</small>}
                    {b.status === "submitted" && hasPermission("finance.approve") && String(b.approval?.current_step?.approver_user_id || "") === String(user?.id || "") && <><button onClick={() => budgetAction(b.id, "approve")}>{t("Approve step")}</button><button onClick={() => budgetAction(b.id, "reject")}>{t("Reject")}</button></>}
                  </div>,
                ])}
              />
            </section>
          )}
          {view === "budgets" && subview === "create" && (
            <section>
              <div className="section-action-bar">
                <div>
                  <h2>{editingBudgetId ? t("Edit budget") : t("Create a budget")}</h2>
                  <p>{t("Plan expected income, expenditure and the resulting surplus or deficit.")}</p>
                </div>
              </div>
              {(hasPermission("finance.create") || (editingBudgetId && hasPermission("finance.approve"))) ? (
                <form
                  className="accounting-card accounting-grid"
                  onSubmit={saveBudget}
                >
                  <input
                    required
                    placeholder={t("Budget name")}
                    value={budget.name}
                    onChange={(e) =>
                      setBudget({ ...budget, name: e.target.value })
                    }
                  />
                  <select
                    required
                    value={budget.financial_year_id}
                    onChange={(e) =>
                      setBudget({
                        ...budget,
                        financial_year_id: e.target.value,
                      })
                    }
                  >
                    <option value="">{t("Financial year")}</option>
                    {years.map((y) => (
                      <option key={y.id} value={y.id}>
                        {y.name}
                      </option>
                    ))}
                  </select>
                  <label className="budget-scope-field">
                    <span>{t("Budget branch / scope")}</span>
                    <select
                      value={budget.branch_id}
                      onChange={(e) =>
                        setBudget({
                          ...budget,
                          branch_id: e.target.value,
                          cost_center_id: "",
                        })
                      }
                    >
                      <option value="">{t("National / all branches")}</option>
                      {branches
                        .filter((b) => b.status === "active")
                        .map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.code} — {b.name}
                          </option>
                        ))}
                    </select>
                    <small>{t("Select the branch that owns this budget, or leave it national.")}</small>
                  </label>
                  <label className="budget-scope-field">
                    <span>{t("Cost centre")}</span>
                    <select
                      value={budget.cost_center_id}
                      onChange={(e) =>
                        setBudget({ ...budget, cost_center_id: e.target.value })
                      }
                    >
                      <option value="">{t("All cost centres")}</option>
                      {costCenters
                        .filter(
                          (c) =>
                            c.status === "active" &&
                            (!budget.branch_id ||
                              String(c.branch_id) === String(budget.branch_id)),
                        )
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.code} — {c.name}
                          </option>
                        ))}
                    </select>
                  </label>
                  <div className="budget-plan-editor">
                    <div className="budget-plan-heading">
                      <div><h3>{t("Income and expenditure plan")}</h3><p>{t("Add all expected income sources and planned expenses.")}</p></div>
                      <div>
                        <button type="button" onClick={() => setBudget({...budget, lines:[...budget.lines,{kind:"income",account_id:"",branch_id:budget.branch_id||"",approved_amount:"",description:""}]})}>+ {t("Income line")}</button>
                        <button type="button" onClick={() => setBudget({...budget, lines:[...budget.lines,{kind:"expense",account_id:"",branch_id:budget.branch_id||"",approved_amount:"",description:""}]})}>+ {t("Expense line")}</button>
                      </div>
                    </div>
                    {budget.lines.map((line, index) => (
                      <div className={`budget-plan-line ${line.kind}`} key={index}>
                        <span>{line.kind === "income" ? t("INCOME") : t("EXPENSE")}</span>
                        <AccountSelect label={line.kind === "income" ? t("Income account") : t("Expense account")} value={line.account_id} accounts={accounts.filter((a) => a.account_type === line.kind && a.status === "active")} onChange={(value) => setBudget({...budget,lines:budget.lines.map((item,i)=>i===index?{...item,account_id:value}:item)})} />
                        <select aria-label={t("Item branch")} value={line.branch_id || ""} onChange={(e)=>setBudget({...budget,lines:budget.lines.map((item,i)=>i===index?{...item,branch_id:e.target.value}:item)})}>
                          <option value="">{t("National / all branches")}</option>
                          {branches.filter((branch)=>branch.status==="active").map((branch)=><option key={branch.id} value={branch.id}>{branch.code} — {branch.name}</option>)}
                        </select>
                        <input required type="number" min="0.01" step="0.01" placeholder={t("Planned amount")} value={line.approved_amount} onChange={(e)=>setBudget({...budget,lines:budget.lines.map((item,i)=>i===index?{...item,approved_amount:e.target.value}:item)})} />
                        <input placeholder={t("Description")} value={line.description} onChange={(e)=>setBudget({...budget,lines:budget.lines.map((item,i)=>i===index?{...item,description:e.target.value}:item)})} />
                        {budget.lines.length > 2 && <button type="button" className="remove-line" onClick={()=>setBudget({...budget,lines:budget.lines.filter((_,i)=>i!==index)})}>×</button>}
                      </div>
                    ))}
                    <div className="budget-plan-totals">
                      <span>{t("Expected income")}: <b>TZS {money(budget.lines.filter(l=>l.kind==="income").reduce((sum,l)=>sum+Number(l.approved_amount||0),0))}</b></span>
                      <span>{t("Planned expenditure")}: <b>TZS {money(budget.lines.filter(l=>l.kind==="expense").reduce((sum,l)=>sum+Number(l.approved_amount||0),0))}</b></span>
                      <span>{t("Expected balance")}: <b>TZS {money(budget.lines.reduce((sum,l)=>sum+(l.kind==="income"?1:-1)*Number(l.approved_amount||0),0))}</b></span>
                    </div>
                  </div>
                  <button className="primary">
                    {editingBudgetId ? t("Save budget changes") : t("Save draft budget")}
                  </button>
                  {editingBudgetId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingBudgetId(null);
                        navigate("/accounting/budgets/overview");
                      }}
                    >
                      {t("Cancel")}
                    </button>
                  )}
                </form>
              ) : (
                <div className="accounting-error">
                  {t("You do not have permission to create budgets.")}
                </div>
              )}
            </section>
          )}
          {view === "budgets" && subview === "approvals" && (
            <section>
              <div className="section-action-bar">
                <div>
                  <h2>{t("Budget approvals")}</h2>
                  <p>{t("Review budgets submitted for authorization.")}</p>
                </div>
              </div>
              <Table
                headers={[t("Budget"), t("Financial Year"), t("Status"), t("Decision")]}
                rows={budgets
                  .filter((b) => b.status === "submitted")
                  .map((b) => [
                    b.name,
                    b.financial_year_id,
                    b.status,
                    <div className="row-actions">
                      {hasPermission("finance.approve") && (
                        <>
                          <button onClick={() => budgetAction(b.id, "approve")}>
                            {t("Approve")}
                          </button>
                          <button onClick={() => budgetAction(b.id, "reject")}>
                            {t("Reject")}
                          </button>
                        </>
                      )}
                    </div>,
                  ])}
              />
            </section>
          )}
          {view === "budgets" && subview === "import" && (
            <section>
              <div className="section-action-bar">
                <div>
                  <h2>{t("Import budgets from CSV")}</h2>
                  <p>
                    {t(
                      "Download the sample, retain its column names, then validate the file before importing."
                    )}
                  </p>
                </div>
                <button
                  className="secondary-link import-download"
                  onClick={() =>
                    api.downloadBudgetSample().catch((e) => setError(e.message))
                  }
                >
                  {t("Download CSV sample")}
                </button>
              </div>
              <div className="accounting-card import-guide">
                <h3>{t("Required CSV format")}</h3>
                <code>
                  budget_name, financial_year, branch_code, account_code,
                  approved_amount, description
                </code>
                <p>
                  <b>financial_year</b>{" "}
                  {t("may use the exact financial-year name or its four-digit calendar year.")}{" "}
                  <b>account_code</b> {t("must be an active income or expense account.")}{" "}
                  {t("Leave")} <b>branch_code</b> {t("blank for a national budget.")}
                </p>
              </div>
              <form
                className="accounting-card import-form"
                onSubmit={previewBudgetImport}
              >
                <label>
                  {t("Choose completed CSV file")}
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    required
                    onChange={(e) => {
                      setBudgetImportFile(e.target.files[0] || null);
                      setBudgetImportPreview(null);
                    }}
                  />
                </label>
                <button className="primary" disabled={budgetImporting}>
                  {budgetImporting ? t("Validating…") : t("Validate & preview")}
                </button>
              </form>
              {budgetImportPreview && (
                <div className="accounting-card">
                  <div
                    className={`import-result ${budgetImportPreview.ready ? "ready" : "invalid"}`}
                  >
                    <strong>
                      {budgetImportPreview.ready
                        ? t("File is ready to import")
                        : t("Correct the errors before importing")}
                    </strong>
                    <span>
                      {budgetImportPreview.budget_count} {t("budgets")} ·{" "}
                      {budgetImportPreview.row_count} {t("lines")}
                    </span>
                  </div>
                  {budgetImportPreview.errors?.length > 0 && (
                    <ul className="import-errors">
                      {budgetImportPreview.errors.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                  <Table
                    headers={[
                      t("Budget"),
                      t("Financial Year"),
                      t("Branch"),
                      t("Account"),
                      t("Amount"),
                      t("Description"),
                    ]}
                    rows={(budgetImportPreview.preview || []).map((row) => [
                      row.budget_name,
                      row.financial_year,
                      row.branch_code || t("National"),
                      row.account_code,
                      money(row.approved_amount),
                      row.description,
                    ])}
                  />
                  {budgetImportPreview.ready && (
                    <div className="form-actions">
                      <button
                        className="primary"
                        disabled={budgetImporting}
                        onClick={commitBudgetImport}
                      >
                        {budgetImporting ? t("Importing…") : t("Confirm import")}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
          {view === "budgets" && subview === "performance" && (
            <section>
              <div className="report-banner">
                <div>
                  <span>{t("BUDGET PERFORMANCE")}</span>
                  <h2>{t("Budget vs Actual")}</h2>
                  <p>
                    {t("Actual expenditure comes from posted General Ledger entries.")}
                  </p>
                </div>
              </div>
              <Table
                headers={[
                  t("Budget"),
                  t("Account"),
                  t("Type"),
                  t("Planned"),
                  t("Actual"),
                  t("Committed"),
                  t("Available"),
                  t("Utilization"),
                ]}
                rows={budgetDetails.flatMap((b) =>
                  (b.lines || []).map((line) => [
                    b.name,
                    accounts.find(
                      (a) => String(a.id) === String(line.account_id),
                    )?.name || line.account_id,
                    line.account_type,
                    money(line.approved_amount),
                    money(line.actual_amount),
                    money(line.committed_amount),
                    money(line.available_amount),
                    `${Number(line.approved_amount) ? Math.round(((Number(line.actual_amount) + Number(line.committed_amount)) / Number(line.approved_amount)) * 100) : 0}%`,
                  ]),
                )}
              />
              <div className="budget-period-plans">
                {budgetDetails.map((budget) => (
                  <article className="accounting-card" key={budget.id}>
                    <div className="section-heading-row">
                      <div>
                        <span className="eyebrow">{budget.budget_code}</span>
                        <h3>{budget.name}</h3>
                        <p>{t("Allocate the annual budget across accounting months.")}</p>
                      </div>
                      <span className={`status ${budget.status}`}>{budget.status}</span>
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>{t("Account")}</th>
                            {(budget.periods || []).map((period) => <th key={period.id}>{period.name}</th>)}
                            <th>{t("Annual")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(budget.lines || []).map((line) => (
                            <tr key={line.id}>
                              <td>{line.account_code} — {line.account_name}</td>
                              {(budget.periods || []).map((period) => {
                                const allocation = line.period_allocations?.[period.id];
                                return (
                                  <td key={period.id}>
                                    {budget.status === "draft" && hasPermission("finance.create") ? (
                                      <input
                                        className="period-amount-input"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={allocation ?? ""}
                                        onChange={(event) => changeBudgetPeriod(
                                          budget.id,
                                          line.id,
                                          period.id,
                                          event.target.value,
                                        )}
                                      />
                                    ) : money(allocation || 0)}
                                  </td>
                                );
                              })}
                              <td><strong>{money(line.approved_amount)}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {budget.status === "draft" && hasPermission("finance.create") && (
                      <div className="form-actions">
                        <button className="primary" onClick={() => saveBudgetPeriods(budget)}>
                          {t("Save monthly allocations")}
                        </button>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          )}
          {view === "banking" && subview === "overview" && (
            <section>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card accounting-grid"
                  onSubmit={saveBankAccount}
                >
                  <input
                    required
                    placeholder={t("Account name")}
                    value={bankAccount.name}
                    onChange={(e) =>
                      setBankAccount({ ...bankAccount, name: e.target.value })
                    }
                  />
                  <select
                    value={bankAccount.account_kind}
                    onChange={(e) =>
                      setBankAccount({
                        ...bankAccount,
                        account_kind: e.target.value,
                      })
                    }
                  >
                    <option value="bank">{t("Bank")}</option>
                    <option value="cash">{t("Cash")}</option>
                  </select>
                  <input
                    placeholder={t("Institution")}
                    value={bankAccount.institution}
                    onChange={(e) =>
                      setBankAccount({
                        ...bankAccount,
                        institution: e.target.value,
                      })
                    }
                  />
                  <input
                    placeholder={t("Account number")}
                    value={bankAccount.account_number}
                    onChange={(e) =>
                      setBankAccount({
                        ...bankAccount,
                        account_number: e.target.value,
                      })
                    }
                  />
                  <select
                    required
                    value={bankAccount.ledger_account_id}
                    onChange={(e) =>
                      setBankAccount({
                        ...bankAccount,
                        ledger_account_id: e.target.value,
                      })
                    }
                  >
                    <option value="">{t("Asset ledger account")}</option>
                    {accounts
                      .filter(
                        (a) =>
                          a.account_type === "asset" && a.status === "active",
                      )
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={t("Opening balance")}
                    value={bankAccount.opening_balance}
                    onChange={(e) =>
                      setBankAccount({
                        ...bankAccount,
                        opening_balance: e.target.value,
                      })
                    }
                  />
                  <button className="primary">{t("Add account")}</button>
                </form>
              )}
              <Table
                headers={[
                  t("Account"),
                  t("Kind"),
                  t("Institution"),
                  t("Number"),
                  t("Currency"),
                  t("Opening"),
                  t("Status"),
                ]}
                rows={bankAccounts.map((b) => [
                  b.name,
                  b.account_kind,
                  b.institution || "-",
                  b.account_number || "-",
                  b.currency,
                  money(b.opening_balance),
                  b.status,
                ])}
              />
            </section>
          )}
          {view === "purchase-orders" && (
            <section>
              <div className="report-banner"><div><span>{t("PROCURE TO PAY")}</span><h2>{t("Purchase Orders & Goods Received Notes")}</h2><p>{t("Approve orders, record actual delivery, then create the supplier invoice from the GRN.")}</p></div></div>
              {hasPermission("finance.create")&&<form className="accounting-card" onSubmit={savePurchaseOrder}><div className="accounting-grid"><select required value={purchaseOrder.supplier_id} onChange={e=>setPurchaseOrder({...purchaseOrder,supplier_id:e.target.value})}><option value="">{t("Supplier")}</option>{suppliers.filter(s=>s.status==="active").map(s=><option key={s.id} value={s.id}>{s.supplier_number} — {s.name}</option>)}</select><input required type="date" value={purchaseOrder.order_date} onChange={e=>setPurchaseOrder({...purchaseOrder,order_date:e.target.value})}/><input type="date" value={purchaseOrder.expected_date} onChange={e=>setPurchaseOrder({...purchaseOrder,expected_date:e.target.value})}/><input required placeholder={t("Purpose / description")} value={purchaseOrder.description} onChange={e=>setPurchaseOrder({...purchaseOrder,description:e.target.value})}/></div><h3>{t("Order lines")}</h3>{purchaseOrder.lines.map((line,index)=><div className="journal-line" key={index}><input required placeholder={t("Item description")} value={line.description} onChange={e=>setPurchaseOrder({...purchaseOrder,lines:purchaseOrder.lines.map((x,i)=>i===index?{...x,description:e.target.value}:x)})}/><AccountSelect label={t("Expense / asset account")} value={line.account_id} accounts={accounts.filter(a=>["expense","asset"].includes(a.account_type)&&a.status==="active")} onChange={value=>setPurchaseOrder({...purchaseOrder,lines:purchaseOrder.lines.map((x,i)=>i===index?{...x,account_id:value}:x)})}/><input required type="number" min="0.001" step="0.001" placeholder={t("Quantity")} value={line.quantity} onChange={e=>setPurchaseOrder({...purchaseOrder,lines:purchaseOrder.lines.map((x,i)=>i===index?{...x,quantity:e.target.value}:x)})}/><input required type="number" min="0" step="0.01" placeholder={t("Unit price")} value={line.unit_price} onChange={e=>setPurchaseOrder({...purchaseOrder,lines:purchaseOrder.lines.map((x,i)=>i===index?{...x,unit_price:e.target.value}:x)})}/><label>{t("VAT %")}<input type="number" min="0" max="100" value={line.vat_rate} onChange={e=>setPurchaseOrder({...purchaseOrder,lines:purchaseOrder.lines.map((x,i)=>i===index?{...x,vat_rate:e.target.value}:x)})}/></label>{purchaseOrder.lines.length>1&&<button type="button" onClick={()=>setPurchaseOrder({...purchaseOrder,lines:purchaseOrder.lines.filter((_,i)=>i!==index)})}>×</button>}</div>)}<div className="form-actions"><button type="button" onClick={()=>setPurchaseOrder({...purchaseOrder,lines:[...purchaseOrder.lines,{description:"",account_id:"",quantity:1,unit_price:"",vat_rate:0}]})}>{t("Add line")}</button><button className="primary">{t("Create purchase order")}</button></div></form>}
              {purchaseOrders.map(po=><article className="accounting-card" key={po.id}><div className="section-action-bar"><div><span className="accounting-eyebrow">{po.po_number}</span><h3>{po.supplier_name}</h3><p>{po.order_date} · TZS {money(po.total_amount)} · {po.status}</p></div><div className="row-actions">{po.status==="draft"&&<button onClick={()=>purchaseOrderAction(po.id,"submit")}>{t("Submit")}</button>}{po.status==="submitted"&&hasPermission("finance.approve")&&<><button onClick={()=>purchaseOrderAction(po.id,"approve")}>{t("Approve")}</button><button onClick={()=>purchaseOrderAction(po.id,"reject")}>{t("Reject")}</button></>}{["approved","partially_received"].includes(po.status)&&<button className="primary" onClick={()=>beginReceivePurchaseOrder(po)}>{t("Receive goods / GRN")}</button>}</div></div><Table headers={[t("Item"),t("Account"),t("Ordered"),t("Received"),t("Unit price"),t("VAT"),t("Total")]} rows={(po.lines||[]).map(line=>[line.description,`${line.account_code} — ${line.account_name}`,line.quantity,line.received_quantity,money(line.unit_price),money(line.vat_amount),money(Number(line.subtotal)+Number(line.vat_amount))])}/>{receivingPo?.id===po.id&&<form className="accounting-card" onSubmit={receivePurchaseOrder}><div className="accounting-grid"><label>{t("Received date")}<input required type="date" value={receiptForm.received_date} onChange={e=>setReceiptForm({...receiptForm,received_date:e.target.value})}/></label><input placeholder={t("Delivery reference")} value={receiptForm.delivery_reference} onChange={e=>setReceiptForm({...receiptForm,delivery_reference:e.target.value})}/><input placeholder={t("Notes")} value={receiptForm.notes} onChange={e=>setReceiptForm({...receiptForm,notes:e.target.value})}/></div>{receiptForm.lines.map((line,index)=><label key={line.purchase_order_line_id}>{line.description} ({t("Remaining")}: {line.remaining})<input type="number" min="0" max={line.remaining} step="0.001" value={line.quantity_received} onChange={e=>setReceiptForm({...receiptForm,lines:receiptForm.lines.map((x,i)=>i===index?{...x,quantity_received:e.target.value}:x)})}/></label>)}<div className="form-actions"><button type="button" onClick={()=>setReceivingPo(null)}>{t("Cancel")}</button><button className="primary">{t("Post GRN")}</button></div></form>}{(po.grns||[]).length>0&&<><h4>{t("Goods Received Notes")}</h4><Table headers={[t("GRN"),t("Date"),t("Delivery reference"),t("Status"),t("Action")]} rows={po.grns.map(grn=>[grn.grn_number,grn.received_date,grn.delivery_reference||"—",grn.status,grn.status==="posted"&&<button onClick={()=>setInvoicingGrn(grn)}>{t("Create supplier invoice")}</button>])}/></>}</article>)}
              {invoicingGrn&&<form className="accounting-card accounting-grid" onSubmit={invoiceGrn}><h3>{t("Create invoice from")} {invoicingGrn.grn_number}</h3><label>{t("Invoice date")}<input required type="date" value={grnInvoiceForm.invoice_date} onChange={e=>setGrnInvoiceForm({...grnInvoiceForm,invoice_date:e.target.value})}/></label><label>{t("Due date")}<input required type="date" value={grnInvoiceForm.due_date} onChange={e=>setGrnInvoiceForm({...grnInvoiceForm,due_date:e.target.value})}/></label><AccountSelect label={t("Accounts payable control")} value={grnInvoiceForm.control_account_id} accounts={accounts.filter(a=>a.account_type==="liability"&&a.status==="active")} onChange={value=>setGrnInvoiceForm({...grnInvoiceForm,control_account_id:value})}/><AccountSelect label={t("VAT input account")} value={grnInvoiceForm.tax_account_id} accounts={accounts.filter(a=>a.account_type==="asset"&&a.status==="active")} onChange={value=>setGrnInvoiceForm({...grnInvoiceForm,tax_account_id:value})}/><button type="button" onClick={()=>setInvoicingGrn(null)}>{t("Cancel")}</button><button className="primary">{t("Create draft supplier invoice")}</button></form>}
            </section>
          )}
          {view === "fixed-assets" && (
            <section>
              <div className="report-banner"><div><span>{t("ASSET REGISTER")}</span><h2>{t("Fixed Assets & Depreciation")}</h2><p>{t("Straight-line depreciation creates a controlled draft General Ledger journal.")}</p></div></div>
              {hasPermission("finance.create")&&<form className="accounting-card accounting-grid" onSubmit={saveFixedAsset}><input placeholder={t("Asset code (automatic if blank)")} value={fixedAsset.asset_code} onChange={e=>setFixedAsset({...fixedAsset,asset_code:e.target.value})}/><input required placeholder={t("Asset name")} value={fixedAsset.name} onChange={e=>setFixedAsset({...fixedAsset,name:e.target.value})}/><input placeholder={t("Description")} value={fixedAsset.description} onChange={e=>setFixedAsset({...fixedAsset,description:e.target.value})}/><label>{t("Purchase date")}<input required type="date" value={fixedAsset.purchase_date} onChange={e=>setFixedAsset({...fixedAsset,purchase_date:e.target.value})}/></label><label>{t("In service date")}<input required type="date" value={fixedAsset.in_service_date} onChange={e=>setFixedAsset({...fixedAsset,in_service_date:e.target.value})}/></label><input required type="number" min="0.01" step="0.01" placeholder={t("Cost")} value={fixedAsset.cost} onChange={e=>setFixedAsset({...fixedAsset,cost:e.target.value})}/><input type="number" min="0" step="0.01" placeholder={t("Residual value")} value={fixedAsset.residual_value} onChange={e=>setFixedAsset({...fixedAsset,residual_value:e.target.value})}/><label>{t("Useful life (months)")}<input required type="number" min="1" value={fixedAsset.useful_life_months} onChange={e=>setFixedAsset({...fixedAsset,useful_life_months:e.target.value})}/></label><AccountSelect label={t("Fixed asset account")} value={fixedAsset.asset_account_id} accounts={accounts.filter(a=>a.account_type==="asset"&&a.status==="active")} onChange={value=>setFixedAsset({...fixedAsset,asset_account_id:value})}/><AccountSelect label={t("Accumulated depreciation account")} value={fixedAsset.accumulated_depreciation_account_id} accounts={accounts.filter(a=>a.account_type==="asset"&&a.status==="active")} onChange={value=>setFixedAsset({...fixedAsset,accumulated_depreciation_account_id:value})}/><AccountSelect label={t("Depreciation expense account")} value={fixedAsset.depreciation_expense_account_id} accounts={accounts.filter(a=>a.account_type==="expense"&&a.status==="active")} onChange={value=>setFixedAsset({...fixedAsset,depreciation_expense_account_id:value})}/><select value={fixedAsset.branch_id} onChange={e=>setFixedAsset({...fixedAsset,branch_id:e.target.value})}><option value="">{t("National")}</option>{branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}</select><button className="primary">{t("Add asset")}</button></form>}
              <Table headers={[t("Code"),t("Asset"),t("Cost"),t("Accumulated depreciation"),t("Net book value"),t("Monthly depreciation"),t("Status"),t("Action")]} rows={fixedAssets.map(asset=>[asset.asset_code,asset.name,money(asset.cost),money(asset.accumulated_depreciation),money(asset.net_book_value),money(asset.monthly_depreciation),asset.status,asset.status==="active"&&hasPermission("finance.create")&&<button onClick={()=>depreciateAsset(asset)}>{t("Run depreciation")}</button>])}/>
            </section>
          )}
          {view === "banking" && subview === "transfers" && (
            <section>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card accounting-grid"
                  onSubmit={saveTransfer}
                >
                  <input
                    required
                    type="date"
                    value={transfer.transfer_date}
                    onChange={(e) =>
                      setTransfer({
                        ...transfer,
                        transfer_date: e.target.value,
                      })
                    }
                  />
                  <select
                    required
                    value={transfer.from_bank_account_id}
                    onChange={(e) =>
                      setTransfer({
                        ...transfer,
                        from_bank_account_id: e.target.value,
                      })
                    }
                  >
                    <option value="">{t("From account")}</option>
                    {bankAccounts
                      .filter((b) => b.status === "active")
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                  </select>
                  <select
                    required
                    value={transfer.to_bank_account_id}
                    onChange={(e) =>
                      setTransfer({
                        ...transfer,
                        to_bank_account_id: e.target.value,
                      })
                    }
                  >
                    <option value="">{t("To account")}</option>
                    {bankAccounts
                      .filter((b) => b.status === "active")
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                  </select>
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder={t("Amount")}
                    value={transfer.amount}
                    onChange={(e) =>
                      setTransfer({ ...transfer, amount: e.target.value })
                    }
                  />
                  <input
                    placeholder={t("Reference")}
                    value={transfer.reference_number}
                    onChange={(e) =>
                      setTransfer({
                        ...transfer,
                        reference_number: e.target.value,
                      })
                    }
                  />
                  <input
                    required
                    placeholder={t("Description")}
                    value={transfer.description}
                    onChange={(e) =>
                      setTransfer({ ...transfer, description: e.target.value })
                    }
                  />
                  <button className="primary">{t("Create transfer")}</button>
                </form>
              )}
              <Table
                headers={[
                  t("Transfer"),
                  t("Date"),
                  t("From"),
                  t("To"),
                  t("Amount"),
                  t("Status"),
                  t("Actions"),
                ]}
                rows={bankTransfers.map((tr) => [
                  tr.transfer_number,
                  tr.transfer_date,
                  bankAccounts.find(
                    (b) => String(b.id) === String(tr.from_bank_account_id),
                  )?.name || tr.from_bank_account_id,
                  bankAccounts.find(
                    (b) => String(b.id) === String(tr.to_bank_account_id),
                  )?.name || tr.to_bank_account_id,
                  money(tr.amount),
                  tr.status,
                  <div className="row-actions">
                    <button
                      onClick={() =>
                        api
                          .printBankTransfer(tr.id)
                          .catch((e) => setError(e.message))
                      }
                    >
                      {t("Print")}
                    </button>
                    {tr.status === "draft" &&
                      hasPermission("finance.create") && (
                        <button onClick={() => transferAction(tr.id, "submit")}>
                          {t("Submit")}
                        </button>
                      )}
                    {tr.status === "submitted" &&
                      hasPermission("finance.approve") && (
                        <>
                          <button
                            onClick={() => transferAction(tr.id, "approve")}
                          >
                            {t("Approve")}
                          </button>
                          <button
                            onClick={() => transferAction(tr.id, "reject")}
                          >
                            {t("Reject")}
                          </button>
                        </>
                      )}
                    {tr.status === "approved" &&
                      hasPermission("finance.post") && (
                        <button onClick={() => transferAction(tr.id, "post")}>
                          {t("Post")}
                        </button>
                      )}
                  </div>,
                ])}
              />
            </section>
          )}
          {view === "banking" && subview === "book" && (
            <section>
              <form className="report-toolbar" onSubmit={loadBankBook}>
                <label>
                  {t("Account")}
                  <select
                    required
                    value={bookFilter.bank_account_id}
                    onChange={(e) =>
                      setBookFilter({
                        ...bookFilter,
                        bank_account_id: e.target.value,
                      })
                    }
                  >
                    <option value="">{t("Choose account")}</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t("From")}
                  <input
                    type="date"
                    value={bookFilter.from}
                    onChange={(e) =>
                      setBookFilter({ ...bookFilter, from: e.target.value })
                    }
                  />
                </label>
                <label>
                  {t("To")}
                  <input
                    type="date"
                    value={bookFilter.to}
                    onChange={(e) =>
                      setBookFilter({ ...bookFilter, to: e.target.value })
                    }
                  />
                </label>
                <button className="primary">{t("View book")}</button>
                <button
                  type="button"
                  className="secondary-link"
                  onClick={() => window.print()}
                >
                  {t("Print")}
                </button>
              </form>
              {bankBook && (
                <>
                  <div className="finance-kpis report-kpis">
                    <Kpi
                      label={t("Account")}
                      value={bankBook.account.name}
                      tone="green"
                    />
                    <Kpi
                      label={t("Opening balance")}
                      value={`${bankBook.account.currency} ${money(bankBook.opening_balance)}`}
                      tone="blue"
                    />
                    <Kpi
                      label={t("Closing balance")}
                      value={`${bankBook.account.currency} ${money(bankBook.closing_balance)}`}
                      tone="purple"
                    />
                  </div>
                  <Table
                    headers={[
                      t("Date"),
                      t("Journal"),
                      t("Reference"),
                      t("Description"),
                      t("Money In"),
                      t("Money Out"),
                      t("Balance"),
                    ]}
                    rows={(bankBook.transactions || []).map((r) => [
                      r.transaction_date,
                      r.journal_number,
                      r.reference_number || "-",
                      r.description || r.journal_description,
                      money(r.debit),
                      money(r.credit),
                      money(r.running_balance),
                    ])}
                  />
                </>
              )}
            </section>
          )}
          {view === "banking" && subview === "statement-import" && (
            <section>
              <div className="section-action-bar">
                <div>
                  <h2>{t("Import bank statement")}</h2>
                  <p>
                    {t("Download the sample, validate the CSV, then confirm the import.")}
                  </p>
                </div>
                <button
                  className="secondary-link"
                  disabled={!statement.bank_account_id}
                  onClick={() =>
                    api
                      .downloadBankStatementSample(statement.bank_account_id)
                      .catch((e) => setError(e.message))
                  }
                >
                  {t("Download CSV sample")}
                </button>
              </div>
              <form
                className="accounting-card import-form"
                onSubmit={previewStatementImport}
              >
                <label>
                  {t("Bank account")}
                  <select
                    required
                    value={statement.bank_account_id}
                    onChange={(e) => {
                      setStatement({
                        ...statement,
                        bank_account_id: e.target.value,
                      });
                      setStatementImportPreview(null);
                    }}
                  >
                    <option value="">{t("Choose account")}</option>
                    {bankAccounts
                      .filter((b) => b.status === "active")
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  {t("Statement CSV")}
                  <input
                    required
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      setStatementImportFile(e.target.files[0] || null);
                      setStatementImportPreview(null);
                    }}
                  />
                </label>
                <button className="primary" disabled={statementImporting}>
                  {statementImporting ? t("Validating…") : t("Validate & preview")}
                </button>
              </form>
              {statementImportPreview && (
                <div className="accounting-card">
                  <div
                    className={`import-result ${statementImportPreview.ready ? "ready" : "invalid"}`}
                  >
                    <strong>
                      {statementImportPreview.ready
                        ? t("File is ready to import")
                        : t("Correct the errors before importing")}
                    </strong>
                    <span>{statementImportPreview.row_count} {t("transactions")}</span>
                  </div>
                  {statementImportPreview.errors?.length > 0 && (
                    <ul className="import-errors">
                      {statementImportPreview.errors.map((x, i) => (
                        <li key={i}>{x}</li>
                      ))}
                    </ul>
                  )}
                  <Table
                    headers={[
                      t("Date"),
                      t("Reference"),
                      t("Description"),
                      t("Direction"),
                      t("Amount"),
                    ]}
                    rows={(statementImportPreview.preview || []).map((r) => [
                      r.transaction_date,
                      r.reference_number || "-",
                      r.description,
                      r.direction,
                      money(r.amount),
                    ])}
                  />
                  {statementImportPreview.ready && (
                    <button
                      className="primary"
                      disabled={statementImporting}
                      onClick={commitStatementImport}
                    >
                      {statementImporting ? t("Importing…") : t("Confirm import")}
                    </button>
                  )}
                </div>
              )}
              <h2 className="section-heading">{t("Add a single statement row")}</h2>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card accounting-grid"
                  onSubmit={saveStatement}
                >
                  <input
                    required
                    type="date"
                    value={statement.transaction_date}
                    onChange={(e) =>
                      setStatement({
                        ...statement,
                        transaction_date: e.target.value,
                      })
                    }
                  />
                  <input
                    placeholder={t("Reference")}
                    value={statement.reference_number}
                    onChange={(e) =>
                      setStatement({
                        ...statement,
                        reference_number: e.target.value,
                      })
                    }
                  />
                  <input
                    required
                    placeholder={t("Description")}
                    value={statement.description}
                    onChange={(e) =>
                      setStatement({
                        ...statement,
                        description: e.target.value,
                      })
                    }
                  />
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder={t("Amount")}
                    value={statement.amount}
                    onChange={(e) =>
                      setStatement({ ...statement, amount: e.target.value })
                    }
                  />
                  <select
                    value={statement.direction}
                    onChange={(e) =>
                      setStatement({ ...statement, direction: e.target.value })
                    }
                  >
                    <option value="credit">{t("Money in")}</option>
                    <option value="debit">{t("Money out")}</option>
                  </select>
                  <button className="primary">{t("Add row")}</button>
                </form>
              )}
            </section>
          )}
          {view === "banking" && subview === "reconciliation" && (
            <section>
              <div className="section-action-bar"><div><h2>{t("Bank matching rules")}</h2><p>{t("Use amount, direction, text and date tolerance to suggest ledger matches.")}</p></div></div>
              {hasPermission("finance.create") && <form className="accounting-card accounting-grid" onSubmit={saveMatchingRule}>
                <input required placeholder={t("Rule name")} value={matchingRule.name} onChange={e=>setMatchingRule({...matchingRule,name:e.target.value})}/>
                <select value={matchingRule.bank_account_id} onChange={e=>setMatchingRule({...matchingRule,bank_account_id:e.target.value})}><option value="">{t("All bank accounts")}</option>{bankAccounts.map(account=><option key={account.id} value={account.id}>{account.name}</option>)}</select>
                <select value={matchingRule.direction} onChange={e=>setMatchingRule({...matchingRule,direction:e.target.value})}><option value="any">{t("Money in or out")}</option><option value="credit">{t("Money in")}</option><option value="debit">{t("Money out")}</option></select>
                <input placeholder={t("Description contains")} value={matchingRule.description_contains} onChange={e=>setMatchingRule({...matchingRule,description_contains:e.target.value})}/>
                <label>{t("Date tolerance (days)")}<input type="number" min="0" max="31" value={matchingRule.date_tolerance_days} onChange={e=>setMatchingRule({...matchingRule,date_tolerance_days:e.target.value})}/></label>
                <button className="primary">{t("Add matching rule")}</button>
              </form>}
              <Table headers={[t("Rule"),t("Account"),t("Direction"),t("Text"),t("Days"),t("Status"),t("Actions")]} rows={bankMatchingRules.map(rule=>[rule.name,bankAccounts.find(a=>String(a.id)===String(rule.bank_account_id))?.name||t("All accounts"),rule.direction,rule.description_contains||"—",rule.date_tolerance_days,rule.status,hasPermission("finance.update")&&<button onClick={()=>toggleMatchingRule(rule)}>{rule.status==="active"?t("Deactivate"):t("Activate")}</button>])}/>
              <h2 className="section-heading">{t("Bank reconciliations")}</h2>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card accounting-grid"
                  onSubmit={saveReconciliation}
                >
                  <select
                    required
                    value={reconciliation.bank_account_id}
                    onChange={(e) =>
                      setReconciliation({
                        ...reconciliation,
                        bank_account_id: e.target.value,
                      })
                    }
                  >
                    <option value="">{t("Bank account")}</option>
                    {bankAccounts.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                  <input
                    required
                    type="date"
                    value={reconciliation.period_start}
                    onChange={(e) =>
                      setReconciliation({
                        ...reconciliation,
                        period_start: e.target.value,
                      })
                    }
                  />
                  <input
                    required
                    type="date"
                    value={reconciliation.period_end}
                    onChange={(e) =>
                      setReconciliation({
                        ...reconciliation,
                        period_end: e.target.value,
                      })
                    }
                  />
                  <input
                    required
                    type="number"
                    step="0.01"
                    placeholder={t("Statement closing balance")}
                    value={reconciliation.statement_balance}
                    onChange={(e) =>
                      setReconciliation({
                        ...reconciliation,
                        statement_balance: e.target.value,
                      })
                    }
                  />
                  <button className="primary">{t("Prepare reconciliation")}</button>
                </form>
              )}
              <Table
                headers={[
                  t("Account"),
                  t("Period"),
                  t("Statement balance"),
                  t("Status"),
                  t("Actions"),
                ]}
                rows={reconciliations.map((r) => [
                  bankAccounts.find(
                    (b) => String(b.id) === String(r.bank_account_id),
                  )?.name || r.bank_account_id,
                  `${r.period_start} — ${r.period_end}`,
                  money(r.statement_balance),
                  r.status,
                  <div className="row-actions">
                    <button onClick={() => openReconciliation(r.id)}>
                      {t("Review items")}
                    </button>
                    {r.status === "draft" &&
                      hasPermission("finance.create") && (
                        <button
                          onClick={() => reconciliationAction(r.id, "submit")}
                        >
                          {t("Submit")}
                        </button>
                      )}
                    {r.status === "submitted" &&
                      hasPermission("finance.approve") && (
                        <button
                          onClick={() => reconciliationAction(r.id, "approve")}
                        >
                          {t("Approve")}
                        </button>
                      )}
                  </div>,
                ])}
              />
              {selectedReconciliation && (
                <div className="accounting-card invoice-detail">
                  <div className="section-action-bar">
                    <div>
                      <h2>{t("Reconciliation items")}</h2>
                      <p>
                        {selectedReconciliation.period_start} —{" "}
                        {selectedReconciliation.period_end}
                      </p>
                    </div>
                    <div className="row-actions">{selectedReconciliation.status==="draft"&&<button onClick={runAutomaticMatching}>{t("Run automatic matching")}</button>}{selectedReconciliation.status==="draft"&&(selectedReconciliation.items||[]).some(item=>item.suggested_journal_id)&&<button className="primary" onClick={acceptSuggestions}>{t("Accept suggested matches")}</button>}<button onClick={() => setSelectedReconciliation(null)}>{t("Close")}</button></div>
                  </div>
                  <Table
                    headers={[
                      t("Date"),
                      t("Reference"),
                      t("Description"),
                      t("Direction"),
                      t("Amount"),
                      t("Journal"),
                      t("Suggestion"),
                      t("Status"),
                      t("Actions"),
                    ]}
                    rows={(selectedReconciliation.items || []).map((i) => [
                      i.transaction_date,
                      i.reference_number || "-",
                      i.statement_description,
                      i.direction,
                      money(i.amount),
                      i.journal_number || "-",
                      i.suggested_journal_id ? `#${i.suggested_journal_id} · ${i.match_confidence}%` : "—",
                      i.match_status,
                      selectedReconciliation.status === "draft" && (
                        <div className="row-actions">
                          <button
                            onClick={() =>
                              resolveReconciliationItem(i, "matched")
                            }
                          >
                            {t("Match journal")}
                          </button>
                          <button
                            onClick={() =>
                              resolveReconciliationItem(i, "resolved")
                            }
                          >
                            {t("Resolve difference")}
                          </button>
                        </div>
                      ),
                    ])}
                  />
                </div>
              )}
            </section>
          )}
          {view === "settings" && ["overview", "chart-of-accounts"].includes(subview) && (
            <section>
              <h2>{t("Chart of Accounts")}</h2>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card accounting-grid"
                  onSubmit={saveAccount}
                >
                  <input
                    required
                    placeholder={t("Code")}
                    value={account.code}
                    onChange={(e) =>
                      setAccount({ ...account, code: e.target.value })
                    }
                  />
                  <input
                    required
                    placeholder={t("Account name")}
                    value={account.name}
                    onChange={(e) =>
                      setAccount({ ...account, name: e.target.value })
                    }
                  />
                  <select
                    value={account.account_type}
                    onChange={(e) =>
                      setAccount({
                        ...account,
                        account_type: e.target.value,
                        parent_id: "",
                      })
                    }
                  >
                    {["asset", "liability", "equity", "income", "expense"].map(
                      (accType) => (
                        <option key={accType}>{accType}</option>
                      ),
                    )}
                  </select>
                  <select
                    value={account.parent_id}
                    onChange={(e) =>
                      setAccount({ ...account, parent_id: e.target.value })
                    }
                  >
                    <option value="">{t("No parent")}</option>
                    {accounts
                      .filter(
                        (a) =>
                          a.account_type === account.account_type &&
                          String(a.id) !== String(editingAccountId),
                      )
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={t("Opening balance")}
                    value={account.opening_balance}
                    onChange={(e) =>
                      setAccount({
                        ...account,
                        opening_balance: e.target.value,
                      })
                    }
                  />
                  <button className="primary">{editingAccountId ? t("Save changes") : t("Add account")}</button>
                  {editingAccountId && <button type="button" onClick={() => { setEditingAccountId(null); setAccount({code:"",name:"",account_type:"asset",parent_id:"",opening_balance:0}); }}>{t("Cancel")}</button>}
                </form>
              )}
              <Table
                headers={[
                  t("Code"),
                  t("Account"),
                  t("Type"),
                  t("Opening"),
                  t("Status"),
                  t("Actions"),
                ]}
                rows={accounts.map((a) => [
                  a.code,
                  a.name,
                  a.account_type,
                  money(a.opening_balance),
                  a.status,
                  hasPermission("finance.update") && <div className="row-actions"><button onClick={() => editAccount(a)}>{t("Edit")}</button><button onClick={() => toggleAccount(a)}>{a.status === "active" ? t("Deactivate") : t("Activate")}</button></div>,
                ])}
              />
            </section>
          )}
          {view === "settings" && subview === "cost-centres" && (
            <section>
              <h2>{t("Cost Centres")}</h2>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card inline-form"
                  onSubmit={saveCostCenter}
                >
                  <input
                    required
                    placeholder={t("Code")}
                    value={costCenter.code}
                    onChange={(e) =>
                      setCostCenter({ ...costCenter, code: e.target.value })
                    }
                  />
                  <input
                    required
                    placeholder={t("Cost centre name")}
                    value={costCenter.name}
                    onChange={(e) =>
                      setCostCenter({ ...costCenter, name: e.target.value })
                    }
                  />
                  <select
                    value={costCenter.branch_id}
                    onChange={(e) =>
                      setCostCenter({
                        ...costCenter,
                        branch_id: e.target.value,
                      })
                    }
                  >
                    <option value="">{t("National")}</option>
                    {branches
                      .filter((b) => b.status === "active")
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                  </select>
                  <button className="primary">{editingCostCenterId ? t("Save changes") : t("Add cost centre")}</button>
                  {editingCostCenterId && <button type="button" onClick={() => {setEditingCostCenterId(null);setCostCenter({code:"",name:"",branch_id:""});}}>{t("Cancel")}</button>}
                </form>
              )}
              <Table
                headers={[t("Code"), t("Cost Centre"), t("Branch"), t("Status"), t("Actions")]}
                rows={costCenters.map((c) => [
                  c.code,
                  c.name,
                  branches.find((b) => String(b.id) === String(c.branch_id))
                    ?.name || t("National"),
                  c.status,
                  hasPermission("finance.update") && <div className="row-actions"><button onClick={() => editCostCenter(c)}>{t("Edit")}</button><button onClick={() => toggleCostCenter(c)}>{c.status === "active" ? t("Deactivate") : t("Activate")}</button></div>,
                ])}
              />
            </section>
          )}
          {view === "settings" && subview === "financial-years" && (
            <section>
              <h2>{t("Financial Years")}</h2>
              {hasPermission("finance.create") && (
                <form
                  className="accounting-card inline-form"
                  onSubmit={saveYear}
                >
                  <input
                    required
                    placeholder={t("Name e.g. 2026")}
                    value={year.name}
                    onChange={(e) => setYear({ ...year, name: e.target.value })}
                  />
                  <input
                    required
                    type="date"
                    value={year.start_date}
                    onChange={(e) =>
                      setYear({ ...year, start_date: e.target.value })
                    }
                  />
                  <input
                    required
                    type="date"
                    value={year.end_date}
                    onChange={(e) =>
                      setYear({ ...year, end_date: e.target.value })
                    }
                  />
                  <button className="primary">{editingYearId ? t("Save changes") : t("Add year")}</button>
                  {editingYearId && <button type="button" onClick={() => {setEditingYearId(null);setYear({name:"",start_date:"",end_date:""});}}>{t("Cancel")}</button>}
                </form>
              )}
              <Table
                headers={[t("Name"), t("Start"), t("End"), t("Status"), t("Actions")]}
                rows={years.map((y) => [
                  y.name,
                  y.start_date,
                  y.end_date,
                  y.status,
                  <div className="row-actions">{hasPermission("finance.update") && <button onClick={() => editYear(y)}>{t("Edit")}</button>}{hasPermission("finance.approve") && (
                    <button
                      onClick={() =>
                        yearAction(
                          y.id,
                          y.status === "open" ? "close" : "reopen",
                        )
                      }
                    >
                      {y.status === "open" ? t("Close year") : t("Reopen year")}
                    </button>
                  )}</div>,
                ])}
              />
              <h3>{t("Accounting periods")}</h3>
              <p>{t("Closing a month prevents new journals and posting in that period.")}</p>
              <Table
                headers={[t("Period"), t("Financial year"), t("Start"), t("End"), t("Status"), t("Actions")]}
                rows={periods.map((period) => [
                  period.name,
                  years.find((item) => String(item.id) === String(period.financial_year_id))?.name || period.financial_year_id,
                  period.start_date,
                  period.end_date,
                  period.status,
                  hasPermission("finance.approve") && (
                    <button onClick={() => periodAction(period.id, period.status === "open" ? "close" : "reopen")}>
                      {period.status === "open" ? t("Close period") : t("Reopen period")}
                    </button>
                  ),
                ])}
              />
            </section>
          )}
          {view === "settings" && subview === "approval-hierarchy" && (
            <section>
              <h2>{t("Approval Hierarchy")}</h2>
              <p>
                {t("Configure the leaders who approve budgets. Steps are completed in order and the budget preparer cannot approve their own budget.")}
              </p>
              {!editingWorkflowId && hasPermission("finance.approve") && <div className="approval-create-actions"><button className="secondary-link" type="button" onClick={()=>startWorkflow("budget")}>+ {t("Budget flow")}</button><button className="secondary-link" type="button" onClick={()=>startWorkflow("staff_requisition")}>+ {t("Requisition flow")}</button><button className="secondary-link" type="button" onClick={()=>startWorkflow("payment_request")}>+ {t("Payment flow")}</button></div>}
              {editingWorkflowId && <form className="accounting-card approval-flow-form" onSubmit={saveWorkflow}><div className="accounting-grid"><input required placeholder={t("Policy name")} value={workflowForm.name} onChange={e=>setWorkflowForm({...workflowForm,name:e.target.value})}/><select value={workflowForm.entity_type} onChange={e=>setWorkflowForm({...workflowForm,entity_type:e.target.value})}><option value="budget">{t("Budget")}</option><option value="staff_requisition">{t("Staff requisition")}</option><option value="payment_request">{t("Payment request")}</option></select><input required type="number" min="0" step="0.01" placeholder={t("Minimum amount")} value={workflowForm.minimum_amount} onChange={e=>setWorkflowForm({...workflowForm,minimum_amount:e.target.value})}/><input type="number" min="0" step="0.01" placeholder={t("Maximum amount (blank = no limit)")} value={workflowForm.maximum_amount} onChange={e=>setWorkflowForm({...workflowForm,maximum_amount:e.target.value})}/></div><h3>{t("Approval steps")}</h3>{workflowForm.steps.map((step,i)=><div className="approval-step-row" key={i}><b>{i+1}</b><input required placeholder={t("Step name")} value={step.name} onChange={e=>setWorkflowForm({...workflowForm,steps:workflowForm.steps.map((s,x)=>x===i?{...s,name:e.target.value}:s)})}/><select required value={step.approver_user_id} onChange={e=>{const user=workflowApprovers.find(u=>String(u.id)===e.target.value);setWorkflowForm({...workflowForm,steps:workflowForm.steps.map((s,x)=>x===i?{...s,approver_user_id:e.target.value,role_id:user?.role_id||""}:s)})}}><option value="">{t("Select approving leader")}</option>{workflowApprovers.map(u=><option key={u.id} value={u.id}>{u.email} — {u.role_name}</option>)}</select><select value={step.role_id} onChange={e=>setWorkflowForm({...workflowForm,steps:workflowForm.steps.map((s,x)=>x===i?{...s,role_id:e.target.value}:s)})}><option value="">{t("Any role")}</option>{workflowRoles.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}</select>{workflowForm.steps.length>1&&<button type="button" onClick={()=>setWorkflowForm({...workflowForm,steps:workflowForm.steps.filter((_,x)=>x!==i)})}>×</button>}</div>)}<div className="form-actions"><button type="button" onClick={()=>setWorkflowForm({...workflowForm,steps:[...workflowForm.steps,{name:`${t("Approval")} ${workflowForm.steps.length+1}`,role_id:"",approver_user_id:""}]})}>{t("Add step")}</button><button className="primary">{t("Save hierarchy")}</button><button type="button" onClick={()=>setEditingWorkflowId(null)}>{t("Cancel")}</button></div></form>}
              <Table
                headers={[
                  t("Policy"),
                  t("Transaction"),
                  t("Minimum"),
                  t("Maximum"),
                  t("Approval flow"),
                  t("Status"),t("Actions"),
                ]}
                rows={approvalWorkflows.map((w) => [
                  w.name,
                  w.entity_type === "staff_requisition" ? t("Staff Requisition") : w.entity_type === "payment_request" ? t("Payment Request") : t("Budget"),
                  money(w.minimum_amount),
                  w.maximum_amount === null
                    ? t("No limit")
                    : money(w.maximum_amount),
                  (w.steps || []).map((s) => s.approver_email ? `${s.name}: ${s.approver_email}` : (s.role_name || s.name)).join(" → "),
                  w.status,
                  hasPermission("finance.update") && (
                    <div className="row-actions">
                      <button onClick={() => editWorkflow(w)}>{t("Edit")}</button>
                      <button
                        onClick={() =>
                          api
                            .setApprovalWorkflowStatus(
                              w.id,
                              w.status === "active" ? "inactive" : "active",
                            )
                            .then(load)
                            .catch((e) => setError(e.message))
                        }
                      >
                        {w.status === "active" ? t("Deactivate") : t("Activate")}
                      </button>
                    </div>
                  ),
                ])}
              />
            </section>
          )}
          {view === "reports" && (
            <section className="financial-report-sheet">
              <div className="report-toolbar">
                {![
                  "overview",
                  "trial-balance",
                  "cash-bank",
                  "receivables-aging",
                  "payables-aging",
                ].includes(subview) && (
                  <>
                    <label>
                      {t("From")}
                      <input
                        type="date"
                        value={reportDates.from}
                        onChange={(e) =>
                          setReportDates({
                            ...reportDates,
                            from: e.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      {t("To")}
                      <input
                        type="date"
                        value={reportDates.to}
                        onChange={(e) =>
                          setReportDates({ ...reportDates, to: e.target.value })
                        }
                      />
                    </label>
                  </>
                )}
                <button
                  className="secondary-link"
                  onClick={() => window.print()}
                >
                  {t("Print / Save PDF")}
                </button>
                <button
                  className="secondary-link"
                  onClick={() =>
                    downloadCsv(
                      subview,
                      ["overview", "trial-balance"].includes(subview)
                        ? trialBalance
                        : reportData?.lines || asArray(reportData),
                    )
                  }
                >
                  {t("Export CSV")}
                </button>
              </div>
              {reportLoading ? (
                <div className="report-loading">
                  {t("Preparing financial report…")}
                </div>
              ) : (
                <>
                  {["overview", "trial-balance"].includes(subview) && (
                    <>
                      <ReportTitle
                        title={t("Trial Balance")}
                        text={t("Balances calculated from posted double-entry journals only.")}
                      />
                      <Table
                        headers={[
                          t("Code"),
                          t("Account"),
                          t("Opening"),
                          t("Debit"),
                          t("Credit"),
                          t("Balance"),
                        ]}
                        rows={trialBalance.map((a) => [
                          a.code,
                          a.name,
                          money(a.opening_balance),
                          money(a.total_debit),
                          money(a.total_credit),
                          money(
                            Number(a.opening_balance) +
                              Number(a.total_debit) -
                              Number(a.total_credit),
                          ),
                        ])}
                      />
                    </>
                  )}
                  {subview === "general-ledger" && (
                    <>
                      <ReportTitle
                        title={t("General Ledger")}
                        text={`${t("Posted transactions from")} ${reportDates.from} ${t("to")} ${reportDates.to}.`}
                      />
                      <Table
                        headers={[
                          t("Date"),
                          t("Journal"),
                          t("Reference"),
                          t("Account"),
                          t("Description"),
                          t("Debit"),
                          t("Credit"),
                        ]}
                        rows={asArray(reportData).map((r) => [
                          r.transaction_date,
                          r.journal_number,
                          r.reference_number || "-",
                          `${r.account_code} — ${r.account_name}`,
                          r.description || r.journal_description,
                          money(r.debit),
                          money(r.credit),
                        ])}
                      />
                    </>
                  )}
                  {subview === "income-expenditure" && (
                    <>
                      <ReportTitle
                        title={t("Income & Expenditure")}
                        text={`${t("Operating performance from")} ${reportDates.from} ${t("to")} ${reportDates.to}.`}
                      />
                      <div className="finance-kpis report-kpis">
                        <Kpi
                          label={t("Total Income")}
                          value={`TZS ${money(reportData?.total_income)}`}
                          tone="green"
                        />
                        <Kpi
                          label={t("Total Expenditure")}
                          value={`TZS ${money(reportData?.total_expense)}`}
                          tone="orange"
                        />
                        <Kpi
                          label={t("Surplus / Deficit")}
                          value={`TZS ${money(reportData?.surplus_deficit)}`}
                          tone="blue"
                        />
                      </div>
                      <Table
                        headers={[t("Code"), t("Account"), t("Type"), t("Amount")]}
                        rows={(reportData?.lines || [])
                          .filter((r) => Number(r.amount) !== 0)
                          .map((r) => [
                            r.code,
                            r.name,
                            r.account_type,
                            money(r.amount),
                          ])}
                      />
                    </>
                  )}
                  {subview === "financial-position" && (
                    <>
                      <ReportTitle
                        title={t("Statement of Financial Position")}
                        text={`${t("Assets, liabilities and accumulated funds as at")} ${reportData?.as_at || reportDates.to}.`}
                      />
                      <div className="finance-kpis report-kpis">
                        <Kpi
                          label={t("Total Assets")}
                          value={`TZS ${money(reportData?.totals?.asset)}`}
                          tone="green"
                        />
                        <Kpi
                          label={t("Total Liabilities")}
                          value={`TZS ${money(reportData?.totals?.liability)}`}
                          tone="orange"
                        />
                        <Kpi
                          label={t("Accumulated Funds")}
                          value={`TZS ${money(reportData?.totals?.equity)}`}
                          tone="blue"
                        />
                      </div>
                      <Table
                        headers={[t("Code"), t("Account"), t("Type"), t("Balance")]}
                        rows={(reportData?.lines || [])
                          .filter((r) => Number(r.balance) !== 0)
                          .map((r) => [
                            r.code,
                            r.name,
                            r.account_type,
                            money(r.balance),
                          ])}
                      />
                    </>
                  )}
                  {subview === "cash-bank" && (
                    <>
                      <ReportTitle
                        title={t("Cash & Bank Balances")}
                        text={t("Balances linked to posted General Ledger movements.")}
                      />
                      <Table
                        headers={[
                          t("Account"),
                          t("Kind"),
                          t("Institution"),
                          t("Ledger"),
                          t("Currency"),
                          t("Balance"),
                        ]}
                        rows={asArray(reportData).map((r) => [
                          r.name,
                          r.account_kind,
                          r.institution || "-",
                          r.ledger_code,
                          r.currency,
                          money(r.balance),
                        ])}
                      />
                    </>
                  )}
                  {["receivables-aging", "payables-aging"].includes(
                    subview,
                  ) && (
                    <>
                      <ReportTitle
                        title={
                          subview === "receivables-aging"
                            ? t("Accounts Receivable Aging")
                            : t("Accounts Payable Aging")
                        }
                        text={t("Outstanding invoices grouped by the number of days overdue.")}
                      />
                      <Table
                        headers={[
                          t("Invoice"),
                          t("Counterparty"),
                          t("Due Date"),
                          t("Outstanding"),
                          t("Days Overdue"),
                          t("Aging Bucket"),
                        ]}
                        rows={asArray(reportData).map((r) => [
                          r.invoice_number,
                          r.counterparty,
                          r.due_date,
                          money(r.outstanding),
                          r.days_overdue,
                          r.aging_bucket,
                        ])}
                      />
                    </>
                  )}
                  {subview === "transactions" && (
                    <>
                      <ReportTitle
                        title={t("Transaction Register")}
                        text={t("Journal lifecycle, source and approval trail.")}
                      />
                      <Table
                        headers={[
                          t("Date"),
                          t("Journal"),
                          t("Source"),
                          t("Reference"),
                          t("Debit"),
                          t("Credit"),
                          t("Status"),
                        ]}
                        rows={asArray(reportData).map((r) => [
                          r.transaction_date,
                          r.journal_number,
                          r.source_module,
                          r.reference_number || "-",
                          money(r.total_debit),
                          money(r.total_credit),
                          r.status,
                        ])}
                      />
                    </>
                  )}
                  {subview === "contributions" && (
                    <>
                      <ReportTitle
                        title={t("Contribution Report")}
                        text={`${t("Collection and GL posting status for")} ${new Date(reportDates.to).getFullYear()}.`}
                      />
                      <Table
                        headers={[
                          t("Branch"),
                          t("Month"),
                          t("Records"),
                          t("Expected"),
                          t("Collected"),
                          t("Posted"),
                        ]}
                        rows={asArray(reportData).map((r) => [
                          r.branch_name || t("Unassigned"),
                          r.period_month,
                          r.records,
                          money(r.expected_amount),
                          money(r.collected_amount),
                          money(r.posted_amount),
                        ])}
                      />
                    </>
                  )}
                  {subview === "cash-flow" && (
                    <><ReportTitle title={t("Cash Flow Statement")} text={`${reportDates.from} — ${reportDates.to}`} /><div className="finance-kpis report-kpis"><Kpi label={t("Opening cash")} value={`TZS ${money(reportData?.opening_cash)}`} tone="blue"/><Kpi label={t("Operating activities")} value={`TZS ${money(reportData?.activities?.operating)}`} tone="green"/><Kpi label={t("Investing activities")} value={`TZS ${money(reportData?.activities?.investing)}`} tone="orange"/><Kpi label={t("Financing activities")} value={`TZS ${money(reportData?.activities?.financing)}`} tone="purple"/><Kpi label={t("Closing cash")} value={`TZS ${money(reportData?.closing_cash)}`} tone="green"/></div><Table headers={[t("Date"),t("Journal"),t("Activity"),t("Description"),t("Money In"),t("Money Out"),t("Net")]} rows={(reportData?.lines||[]).map(row=>[row.transaction_date,row.journal_number,row.activity,row.description,money(row.money_in),money(row.money_out),money(row.net)])}/></>
                  )}
                  {subview === "comparative" && (
                    <><ReportTitle title={t("Comparative Income & Expenditure")} text={`${reportData?.current?.from||reportDates.from} — ${reportData?.current?.to||reportDates.to} ${t("compared with prior year")}`} /><div className="finance-kpis report-kpis"><Kpi label={t("Current surplus / deficit")} value={`TZS ${money(reportData?.current?.surplus_deficit)}`} tone="green"/><Kpi label={t("Previous surplus / deficit")} value={`TZS ${money(reportData?.previous?.surplus_deficit)}`} tone="blue"/><Kpi label={t("Variance")} value={`TZS ${money(reportData?.variance)}`} tone="orange"/></div><Table headers={[t("Code"),t("Account"),t("Type"),t("Current"),t("Previous"),t("Variance"),t("Variance %")]} rows={(reportData?.lines||[]).map(row=>[row.account_code,row.account_name,row.account_type,money(row.current_amount),money(row.previous_amount),money(row.variance),row.variance_percent===null?"—":`${row.variance_percent}%`])}/></>
                  )}
                  {subview === "audit" && (
                    <>
                      <ReportTitle
                        title={t("Finance Audit Report")}
                        text={t("Finance actions captured by the immutable audit trail.")}
                      />
                      <Table
                        headers={[
                          t("Timestamp"),
                          t("User"),
                          t("Action"),
                          t("Entity"),
                          t("Entity ID"),
                          t("IP"),
                        ]}
                        rows={asArray(reportData).map((r) => [
                          r.created_at,
                          r.user_id,
                          r.action,
                          r.entity_type,
                          r.entity_id,
                          r.ip_address,
                        ])}
                      />
                    </>
                  )}
                </>
              )}
            </section>
          )}
          {documentTarget && (
            <FinanceDocuments
              target={documentTarget}
              documents={financeDocuments}
              form={documentForm}
              setForm={setDocumentForm}
              onUpload={uploadDocument}
              onClose={() => setDocumentTarget(null)}
              canUpload={hasPermission("finance.create")}
            />
          )}
        </>
      )}
    </div>
  );
}

function Table({ headers, rows, pageSize }) {
  const { t } = useLanguage();
  const [page, setPage] = useState(1);
  const totalPages = pageSize ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  const visibleRows = pageSize
    ? rows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : rows;
  const actionHeaders = [t("Action"), t("Actions"), t("Decision")];

  useEffect(() => {
    setPage(1);
  }, [rows.length, pageSize]);

  return (
    <div className="accounting-table-wrap">
      <table className="accounting-table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.length ? (
            visibleRows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, x) => (
                  <td
                    key={x}
                    data-label={headers[x]}
                    className={
                      x === 0 ? "cell-primary" : actionHeaders.includes(headers[x]) ? "cell-actions" : undefined
                    }
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td className="cell-empty" colSpan={headers.length}>{t("No records found.")}</td>
            </tr>
          )}
        </tbody>
      </table>
      {pageSize && rows.length > pageSize && (
        <div
          className="form-actions"
          style={{ alignItems: "center", padding: "10px 12px" }}
        >
          <span style={{ marginRight: "auto", color: "#64748b", fontSize: 13 }}>
            {t("Page")} {currentPage} {t("of")} {totalPages} ({rows.length} {t("total")})
          </span>
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            {t("Previous")}
          </button>
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t("Next")}
          </button>
        </div>
      )}
    </div>
  );
}

function CashForm({
  kind,
  value,
  setValue,
  accounts,
  branches = [],
  costCenters = [],
  budgetDetails = [],
  onSubmit,
}) {
  const { t } = useLanguage();
  const isReceipt = kind === "receipt";
  const targetKey = isReceipt ? "income_account_id" : "expense_account_id";
  const targetTypes = isReceipt
    ? ["income", "liability"]
    : ["expense", "asset", "liability"];
  return (
    <form className="accounting-card accounting-grid" onSubmit={onSubmit}>
      <input
        required
        placeholder={isReceipt ? t("Payer") : t("Payee")}
        value={value[isReceipt ? "payer" : "payee"]}
        onChange={(e) =>
          setValue({
            ...value,
            [isReceipt ? "payer" : "payee"]: e.target.value,
          })
        }
      />
      <input
        required
        type="number"
        min="0.01"
        step="0.01"
        placeholder={t("Amount")}
        value={value.amount}
        onChange={(e) => setValue({ ...value, amount: e.target.value })}
      />
      <input
        required
        type="date"
        value={value[isReceipt ? "receipt_date" : "payment_date"]}
        onChange={(e) =>
          setValue({
            ...value,
            [isReceipt ? "receipt_date" : "payment_date"]: e.target.value,
          })
        }
      />
      <select
        value={value.payment_method}
        onChange={(e) => setValue({ ...value, payment_method: e.target.value })}
      >
        {["cash", "bank_transfer", "mobile_money", "cheque", "other"].map(
          (x) => (
            <option key={x}>{x}</option>
          ),
        )}
      </select>
      <select
        required
        value={value.cash_account_id}
        onChange={(e) =>
          setValue({ ...value, cash_account_id: e.target.value })
        }
      >
        <option value="">{t("Cash/Bank account")}</option>
        {accounts
          .filter((a) => a.status === "active" && a.account_type === "asset")
          .map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} — {a.name}
            </option>
          ))}
      </select>
      <select
        required
        value={value[targetKey]}
        onChange={(e) => setValue({ ...value, [targetKey]: e.target.value })}
      >
        <option value="">
          {isReceipt ? t("Income account") : t("Expense/account")}
        </option>
        {accounts
          .filter(
            (a) =>
              a.status === "active" && targetTypes.includes(a.account_type),
          )
          .map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} — {a.name}
            </option>
          ))}
      </select>
      {!isReceipt && (
        <>
          <select
            value={value.branch_id}
            onChange={(e) =>
              setValue({
                ...value,
                branch_id: e.target.value,
                cost_center_id: "",
                budget_line_id: "",
              })
            }
          >
            <option value="">{t("National / no branch")}</option>
            {branches
              .filter((b) => b.status === "active")
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.code} — {b.name}
                </option>
              ))}
          </select>
          <select
            value={value.cost_center_id}
            onChange={(e) =>
              setValue({
                ...value,
                cost_center_id: e.target.value,
                budget_line_id: "",
              })
            }
          >
            <option value="">{t("No cost centre")}</option>
            {costCenters
              .filter(
                (c) =>
                  c.status === "active" &&
                  (!value.branch_id ||
                    String(c.branch_id) === String(value.branch_id)),
              )
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
          </select>
          <select
            value={value.budget_line_id}
            onChange={(e) =>
              setValue({ ...value, budget_line_id: e.target.value })
            }
          >
            <option value="">{t("No budget commitment")}</option>
            {budgetDetails.flatMap((b) =>
              (b.lines || [])
                .filter(
                  (l) =>
                    String(l.account_id) === String(value.expense_account_id) &&
                    String(b.branch_id || "") ===
                      String(value.branch_id || "") &&
                    String(b.cost_center_id || "") ===
                      String(value.cost_center_id || ""),
                )
                .map((l) => (
                  <option key={l.id} value={l.id}>
                    {b.name} — {t("available")} TZS{" "}
                    {Number(l.available_amount || 0).toLocaleString("en-TZ")}
                  </option>
                )),
            )}
          </select>
        </>
      )}
      <input
        placeholder={t("Reference")}
        value={value.payment_reference}
        onChange={(e) =>
          setValue({ ...value, payment_reference: e.target.value })
        }
      />
      <input
        required={!isReceipt}
        placeholder={t("Description")}
        value={value.description}
        onChange={(e) => setValue({ ...value, description: e.target.value })}
      />
      <button className="primary">{t("Create")} {isReceipt ? t("receipt") : t("payment")}</button>
    </form>
  );
}

function AccountSelect({ label, value, accounts, onChange }) {
  return (
    <select required value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{label}</option>
      {accounts
        .filter((a) => a.status === "active")
        .map((a) => (
          <option key={a.id} value={a.id}>
            {a.code} — {a.name}
          </option>
        ))}
    </select>
  );
}

const receivableViews = ["customers", "receivables", "receipts"];
const payableViews = ["suppliers", "payables", "payments"];
const accountingModules = [
  { to: "/accounting/overview", label: "Overview", icon: "◎", views: ["overview"] },
  { to: "/accounting/receivables", label: "Receivables", icon: "↓", views: receivableViews },
  { to: "/accounting/payables", label: "Payables", icon: "↑", views: payableViews },
  { to: "/accounting/banking", label: "Banking", icon: "▤", views: ["banking"] },
  { to: "/accounting/general-ledger", label: "General Ledger", icon: "≡", views: ["general-ledger"] },
  { to: "/accounting/purchase-orders", label: "Purchase Orders", icon: "▦", views: ["purchase-orders"] },
  { to: "/accounting/fixed-assets", label: "Fixed Assets", icon: "◫", views: ["fixed-assets"] },
  { to: "/accounting/budgets", label: "Budgets", icon: "◔", views: ["budgets"] },
  { to: "/accounting/reports", label: "Reports", icon: "▥", views: ["reports"] },
  { to: "/accounting/settings", label: "Settings", icon: "⚙", views: ["settings"] },
];

function Kpi({ label, value, tone }) {
  return (
    <div className={`finance-kpi ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function Workspace({ to, title, text, icon }) {
  return (
    <Link className="finance-workspace" to={to}>
      <span className="workspace-icon">{icon}</span>
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
      <b>›</b>
    </Link>
  );
}
function ReportTitle({ title, text }) {
  const { t } = useLanguage();
  return (
    <div className="report-banner">
      <div>
        <span>{t("FINANCIAL REPORT")}</span>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
    </div>
  );
}
function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
}
function downloadCsv(name, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const quote = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
  const csv = [
    headers.map(quote).join(","),
    ...rows.map((r) =>
      headers
        .map((h) =>
          quote(typeof r[h] === "object" ? JSON.stringify(r[h]) : r[h]),
        )
        .join(","),
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name}-report.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function FinanceDocuments({
  target,
  documents,
  form,
  setForm,
  onUpload,
  onClose,
  canUpload,
}) {
  const { t } = useLanguage();
  return (
    <div className="accounting-card invoice-detail">
      <div className="section-action-bar">
        <div>
          <span className="accounting-eyebrow">{t("SUPPORTING DOCUMENTS")}</span>
          <h2>{target.label}</h2>
        </div>
        <button onClick={onClose}>{t("Close")}</button>
      </div>
      {canUpload && (
        <form className="import-form" onSubmit={onUpload}>
          <label>
            {t("Document title")}
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>
          <label>
            {t("PDF or Word document")}
            <input
              required
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) =>
                setForm({ ...form, file: e.target.files[0] || null })
              }
            />
          </label>
          <button className="primary">{t("Upload")}</button>
        </form>
      )}
      <Table
        headers={[t("Title"), t("Type"), t("Size"), t("Uploaded"), t("Action")]}
        rows={documents.map((d) => [
          d.title,
          d.mime_type,
          `${Math.ceil(Number(d.file_size) / 1024)} KB`,
          d.created_at,
          <button
            onClick={() =>
              api
                .downloadFinanceDocument(target.type, target.id, d.id, d.title)
                .catch(() => {})
            }
          >
            {t("Download")}
          </button>,
        ])}
      />
    </div>
  );
}

function InvoiceDetail({
  invoice,
  accounts,
  settlement,
  setSettlement,
  creditNote,
  setCreditNote,
  onClose,
  onSaveSettlement,
  onSettlementAction,
  onSaveCreditNote,
  onCreditNoteAction,
  hasPermission,
  money,
}) {
  const { t } = useLanguage();
  const outstanding = Number(invoice.amount) - Number(invoice.paid_amount) - Number(invoice.credited_amount || 0);
  return (
    <div className="invoice-detail accounting-card">
      <div className="section-action-bar">
        <div>
          <span className="accounting-eyebrow">{t("INVOICE DETAIL")}</span>
          <h2>{invoice.invoice_number}</h2>
          <p>
            {invoice.counterparty} · {t("Due")} {invoice.due_date}
          </p>
        </div>
        <div className="row-actions">
          <button onClick={() => api.printInvoice(invoice.id)}>
            {t("Print / PDF")}
          </button>
          <button className="secondary-link" onClick={onClose}>
            {t("Close")}
          </button>
        </div>
      </div>
      <div className="finance-kpis report-kpis">
        <Kpi
          label={t("Invoice amount")}
          value={`TZS ${money(invoice.amount)}`}
          tone="blue"
        />
        <Kpi
          label={t("Paid")}
          value={`TZS ${money(invoice.paid_amount)}`}
          tone="green"
        />
        <Kpi label={t("Credited")} value={`TZS ${money(invoice.credited_amount || 0)}`} tone="purple" />
        <Kpi
          label={t("Outstanding")}
          value={`TZS ${money(outstanding)}`}
          tone="orange"
        />
      </div>
      {(invoice.lines||[]).length>0&&<Table headers={[t("Description"),t("Account"),t("Qty"),t("Unit price"),t("Subtotal"),t("VAT"),t("Withholding"),t("Total")]} rows={invoice.lines.map(line=>[line.description,`${line.account_code} — ${line.account_name}`,line.quantity,money(line.unit_price),money(line.subtotal),`${line.vat_rate}% · ${money(line.vat_amount)}`,`${line.withholding_rate}% · ${money(line.withholding_amount)}`,money(line.total_amount)])}/>} 
      {outstanding > 0 &&
        ["open", "partial"].includes(invoice.status) &&
        hasPermission("finance.create") && (
          <form
            className="accounting-grid invoice-payment-form"
            onSubmit={onSaveSettlement}
          >
            <input
              required
              type="date"
              value={settlement.settlement_date}
              onChange={(e) =>
                setSettlement({
                  ...settlement,
                  settlement_date: e.target.value,
                })
              }
            />
            <input
              required
              type="number"
              min="0.01"
              max={outstanding}
              step="0.01"
              value={settlement.amount}
              onChange={(e) =>
                setSettlement({ ...settlement, amount: e.target.value })
              }
            />
            <AccountSelect
              label={t("Cash / bank ledger")}
              value={settlement.cash_account_id}
              accounts={accounts.filter((a) => a.account_type === "asset")}
              onChange={(v) =>
                setSettlement({ ...settlement, cash_account_id: v })
              }
            />
            <input
              placeholder={t("Payment reference")}
              value={settlement.reference_number}
              onChange={(e) =>
                setSettlement({
                  ...settlement,
                  reference_number: e.target.value,
                })
              }
            />
            <button className="primary">
              {invoice.invoice_type === "receivable"
                ? t("Receive payment")
                : t("Record supplier payment")}
            </button>
          </form>
        )}
      <h3>
        {invoice.invoice_type === "receivable"
          ? t("Receipt allocation history")
          : t("Supplier payment history")}
      </h3>
      <Table
        headers={[t("Date"), t("Reference"), t("Amount"), t("Status"), t("Actions")]}
        rows={(invoice.settlements || []).map((s) => [
          s.settlement_date,
          s.reference_number || "-",
          money(s.amount),
          s.status,
          <div className="row-actions">
            {s.status === "submitted" && hasPermission("finance.post") && (
              <button onClick={() => onSettlementAction(s.id, "post")}>
                {t("Approve & post")}
              </button>
            )}
          </div>,
        ])}
      />
      {outstanding>0&&["open","partial"].includes(invoice.status)&&hasPermission("finance.create")&&<form className="accounting-card accounting-grid" onSubmit={onSaveCreditNote}><input required type="date" value={creditNote.credit_date} onChange={e=>setCreditNote({...creditNote,credit_date:e.target.value})}/><input required type="number" min="0.01" max={outstanding} step="0.01" placeholder={t("Credit amount")} value={creditNote.amount} onChange={e=>setCreditNote({...creditNote,amount:e.target.value})}/><input required placeholder={t("Credit note reason")} value={creditNote.reason} onChange={e=>setCreditNote({...creditNote,reason:e.target.value})}/><button className="primary">{t("Create credit note")}</button></form>}
      <h3>{t("Credit notes")}</h3>
      <Table headers={[t("Credit note"),t("Date"),t("Reason"),t("Amount"),t("Status"),t("Actions")]} rows={(invoice.credit_notes||[]).map(note=>[note.credit_note_number,note.credit_date,note.reason,money(note.amount),note.status,<div className="row-actions">{note.status==="submitted"&&hasPermission("finance.approve")&&<><button onClick={()=>onCreditNoteAction(note.id,"post")}>{t("Approve & post")}</button><button onClick={()=>onCreditNoteAction(note.id,"reject")}>{t("Reject")}</button></>}</div>])}/>
    </div>
  );
}
